import {
  AchievementSystem,
  Achievement,
  AchievementSaveData,
  AchievementContext,
  AchievementType,
  createInitialAchievementState,
} from './achievements';
import { processAssemblerCrafting } from './AssemblerCrafting';
import { addItemToBelt } from './BeltItemSystem';
import {
  Position,
  Building,
  BuildingType,
  BuildingCategory,
  BuildMenuState,
  BeltItem,
  AssemblerInventory,
  ChestInventory,
  RecipeIngredient,
  BuildingItemRequirement,
  ItemType,
  GAME_CONFIG,
  BUILDINGS,
  BUILDINGS_BY_ID,
  BELT_CHARACTERS,
  FAST_BELT_CHARACTERS,
  BeltType,
  ResearchId,
  RESEARCHES,
  RECIPES_BY_ID,
  Recipe,
  createEmptyItemCounts,
  STARTING_PLAYER_ITEMS,
  ITEM_TYPES,
} from './constants';
import { EntityType } from './entities/Entity';
import { EntityManager } from './entities/EntityManager';
import type {
  ItemProductionHistoryEntry,
  PlayerFacing,
  PowerDemandByBuildingType,
  PowerSnapshot,
} from './GameState';
import { getDirectionalBuildingCharacter } from './GameState';
import {
  findBuildingCenter,
  getBuildingAtPosition,
  getAssemblerInventory,
  setAssemblerInventory,
  getMaxInputOutputsMultiplier,
  createEmptyChestInventory,
  getAvailableItemCountsForPlacement,
  consumeItemsForBuilding,
  addItemsToChest,
  createEmptyPlayerInventory,
  addItemToPlayerInventory,
  removeItemsFromPlayerInventory,
  normalizePlayerInventory,
  type ResourceNodeMenuState,
  type MineResourceResult,
  getPlayerInventoryItemCounts,
  type PlayerInventory,
  type CollectBuildingInventoryResult,
} from './GameState';
import {
  HandCraftingQueueEntry,
  calculateHandCraftingQueuedOutputs,
  getHandCraftingTicksPerCraft,
  normalizeHandCraftingQueue,
  planHandCraftingForRequirements,
} from './HandCrafting';
import { TickSystem, getSharedTickSystem } from './hooks/useGameTick';
import {
  InputCommand,
  InputCommandType,
  MoveCommand,
  CollectResourceCommand,
  TakeActionCommand,
} from './InputCommands';
import { InputManager } from './InputManager';
import { buildingLogger, beltLogger, inputLogger } from './Logger';
import {
  PRESTIGE_UPGRADES,
  PrestigeUpgradeState,
  PrestigeUpgradeId,
  createInitialPrestigeUpgradeState,
  selectPrestigeUpgrade as selectPrestigeUpgradeState,
  purchasePrestigeUpgradeState,
  isPrestigeUpgradePurchased,
} from './PrestigeSystem';
import {
  ResearchState,
  createInitialResearchState,
  unlockResearchId,
  isResearchUnlocked as isResearchUnlockedInState,
  isBuildingUnlocked as isBuildingUnlockedInState,
  isRecipeUnlocked as isRecipeUnlockedInState,
  setSelectedResearch,
  setResearchQueue,
  getActiveResearchId,
  addResearchProgress,
  getMinerSpeedMultiplier,
  getAssemblerSpeedMultiplier,
  getFactorySpeedMultiplier,
  getBeltSpeedMultiplier,
  getGeneratorOutputMultiplier,
  getBeltCapacityMultiplier,
  getRepeatableResearchConfig,
  getRepeatableResearchLevel,
} from './ResearchSystem';
import {
  logCollisionSystemState,
  logResourceNodeCollisionIssues,
  logMinerPlacementCollisionIssues,
  logPlayerMovementCollision,
  logCollisionTest,
} from './services/CollisionLogger';
import { CollisionService } from './services/CollisionService';
import {
  getAllEntitiesFromGameState,
  createPlayerEntity,
  updateEntityPosition,
} from './services/EntityConverters';
import MusicEngine, {
  DEFAULT_MUSIC_SETTINGS,
  type MusicSettings,
} from './services/MusicEngine';
import SoundEngine, {
  DEFAULT_SOUND_SETTINGS,
  type SoundSettings,
} from './services/SoundEngine';
import {
  processSmartBeltPlacement,
  recordBeltPlacement,
  getBeltDirectionFromCharacter,
  getDirectionBetweenPositions,
} from './SmartBeltPlacement';
import {
  loadDebugMenuUIState,
  saveDebugMenuUIState,
} from './utils/DebugMenuStorage';
import {
  applyMusicSettingsDefaults,
  loadMusicSettings,
  saveMusicSettings,
} from './utils/MusicSettingsStorage';
import {
  applySoundSettingsDefaults,
  loadSoundSettings,
  saveSoundSettings,
} from './utils/SoundSettingsStorage';
import {
  secondsToTicks,
  TICK_DURATION_MS,
  TICK_INTERVALS,
} from './utils/TickIntervals';
import {
  generateEntitiesForPosition,
  generateTerrainTile,
} from './WorldGeneration';

interface PreparedHandCraftingPlan {
  readonly playerCounts: Record<ItemType, number>;
  readonly consumptionEntries: ReadonlyArray<readonly [ItemType, number]>;
  readonly preparedCrafts: ReadonlyArray<{ recipe: Recipe; crafts: number }>;
}

interface HandCraftingLogContext {
  source: 'building' | 'manual';
  buildingId?: Building['id'];
  item?: ItemType;
  amount?: number;
}

const BELT_COLLECT_RADIUS = 2;
const REJECTION_DISPLAY_DURATION_TICKS = secondsToTicks(2);
const PRODUCTION_HISTORY_MAX_WINDOW_TICKS = secondsToTicks(60 * 10);
const UNDERGROUND_BELT_MAX_DISTANCE = 6;
const LEGACY_MS_TIMESTAMP_THRESHOLD = 1_000_000_000;

export interface QuickBeltPreviewSegment {
  readonly position: Position;
  readonly direction: PlayerFacing;
}

export type QuickBeltPreviewStatus = 'dragging' | 'confirm';

export interface QuickBeltPreviewState {
  readonly beltType: BeltType;
  readonly segments: QuickBeltPreviewSegment[];
  readonly status: QuickBeltPreviewStatus;
  readonly cost: BuildingItemRequirement[];
  readonly isValid: boolean;
  readonly invalidReason?: string;
}

export interface QuickBeltPlacementResult {
  readonly success: boolean;
  readonly placedCount?: number;
  readonly error?: string;
}

// Core game state interface - centralized and not dependent on React
export interface CoreGameState {
  // Player state
  playerPos: Position;
  playerInventory: PlayerInventory;
  playerFacing: PlayerFacing;
  lastMovementTick: number;
  hoverTargetPosition: Position | null;

  // Camera and UI state
  cameraPos: Position;
  zoomLevel: number;
  currentBiome: string;
  tickSpeedMultiplier: number;

  // Build menu state
  buildMenuOpen: BuildMenuState;
  selectedCategory: BuildingCategory | null;
  selectedBuilding: Building | null;

  // UI menu states
  helpMenuOpen: boolean;
  saveMenuOpen: boolean;
  leftPanelOpen: boolean;
  debugMenuOpen: boolean;
  achievementMenuOpen: boolean;
  researchMenuOpen: boolean;
  productionMenuOpen: boolean;

  // Building status menu target
  buildingStatusMenuTarget: { x: number; y: number } | null;
  resourceNodeMenu: ResourceNodeMenuState | null;

  // Debug/cheat tracking
  hasEnabledDebug: boolean;

  // Achievement system
  achievements: Achievement[];
  achievementSaveData: AchievementSaveData;
  researchState: ResearchState;
  prestigePoints: number;
  prestigeCount: number;

  // World data
  gameMap: Record<string, string>;
  buildingLayer: Record<string, string>;
  beltContentLayer: Record<string, BeltItem[]>;
  assemblerInventoryLayer: Record<string, AssemblerInventory>;
  chestInventoryLayer: Record<string, ChestInventory>;
  rejectedItemsLayer: Record<string, { timestamp: number; itemType: ItemType }>;
  minerTimerLayer: Record<string, number>;
  minerOutputBufferLayer: Record<string, number>;
  powerProduction: number;
  powerDemand: number;
  powerSatisfaction: number;
  powerHistory: PowerSnapshot[];
  powerDemandByBuildingType: PowerDemandByBuildingType;
  itemProductionHistory: ItemProductionHistoryEntry[];
  currentTick: number;
  handCraftingQueue: HandCraftingQueueEntry[];

  // Prestige system
  prestigeUpgradeState: PrestigeUpgradeState;
  prestigeMenuOpen: boolean;

  // Quick belt placement preview
  quickBeltPreview: QuickBeltPreviewState | null;
}

export interface GameEngineOptions {
  tickSystem?: TickSystem;
}

// Event types for UI updates
export type GameEngineEvent =
  | 'stateChanged'
  | 'playerMoved'
  | 'buildingPlaced'
  | 'buildingDeconstructed'
  | 'itemProduced'
  | 'craftingCompleted'
  | 'hoverTargetChanged';

export type GameEngineEventCallback = (state: CoreGameState) => void;

export type UpgradeAssemblerResult =
  | { success: true }
  | {
      success: false;
      reason:
        | 'not-assembler'
        | 'factory-locked'
        | 'insufficient-items'
        | 'consumption-failed';
    };

// Movement command for queuing
interface QueuedMovementCommand {
  direction: { x: number; y: number };
  timestamp: number;
}

// Game engine class - manages all game state and logic
export class GameEngine {
  private state!: CoreGameState;
  private eventListeners: Map<GameEngineEvent, Set<GameEngineEventCallback>>;
  private isTickProcessing: boolean = false;
  private movementQueue: QueuedMovementCommand[] = [];
  private inputManager: InputManager;
  private collisionService: CollisionService;
  private readonly musicEngine: MusicEngine;
  private readonly soundEngine: SoundEngine;
  private musicSettings: MusicSettings = DEFAULT_MUSIC_SETTINGS;
  private soundSettings: SoundSettings = DEFAULT_SOUND_SETTINGS;
  private achievementSystem: AchievementSystem;
  private entityManager: EntityManager;
  private readonly tickSystem: TickSystem;
  private unlimitedPowerEnabled: boolean = false;
  private ignoreBuildingCostsEnabled: boolean = false;
  private generatedEntityPositions: Set<string>;
  private static readonly TICK_SPEED_OPTIONS = [1, 2, 4];
  private static readonly POWER_HISTORY_LENGTH = 120;
  private static readonly POWER_HISTORY_INTERVAL_TICKS = secondsToTicks(2);
  private static readonly POWER_HUNGRY_THRESHOLD_MW = 10;
  private static readonly FLOW_STATE_MACHINE_THRESHOLD = 20;
  private static readonly POWER_UPTIME_TARGET_TICKS = secondsToTicks(60 * 10);
  private static readonly RESEARCH_DATA_TARGET = 1000;
  private static readonly RESEARCH_COMPLETION_TARGET = 10;
  private static readonly ORE_EXTRACTION_TARGET = 10_000;
  private static readonly IRON_PLATE_TARGET = 10_000;
  private lastPowerHistoryRecordTick: number | null = null;
  private quickBeltAnchor: Position | null = null;
  private powerUptimeStreakTicks: number = 0;
  private totalResearchDataProduced: number = 0;
  private totalOreExtracted: number = 0;
  private totalIronPlatesProduced: number = 0;

  constructor(
    initialState?: Partial<CoreGameState>,
    options: GameEngineOptions = {}
  ) {
    this.eventListeners = new Map();

    this.tickSystem = options.tickSystem ?? getSharedTickSystem();

    // Initialize achievement system
    this.achievementSystem = AchievementSystem.getInstance({
      showNotifications: true,
      achievementsDisabled: false, // Will be updated in initializeGameState
    });

    // Initialize input manager
    this.inputManager = InputManager.getInstance();
    this.inputManager.startListening();

    // Initialize collision system
    this.collisionService = new CollisionService(16); // 16x16 cell size for spatial grid

    // Set up collision system rebuild callback
    this.collisionService.setRebuildCallback(() => {
      this.initializeCollisionSystem();
    });

    // Initialize entity manager BEFORE collision system setup (getTile needs it)
    this.entityManager = new EntityManager();
    this.generatedEntityPositions = new Set();

    // Set up EntityManager event forwarding to React and mark collision system dirty
    this.entityManager.addEventListener('entityAdded', () => {
      this.collisionService.markDirty();
      this.emitEvent('stateChanged');
    });
    this.entityManager.addEventListener('entityRemoved', () => {
      this.collisionService.markDirty();
      this.emitEvent('stateChanged');
    });
    this.entityManager.addEventListener('entityUpdated', () => {
      this.collisionService.markDirty();
      this.emitEvent('stateChanged');
    });

    this.musicSettings = loadMusicSettings();
    this.musicEngine = new MusicEngine(undefined, this.musicSettings);

    this.soundSettings = loadSoundSettings();
    this.soundEngine = new SoundEngine(this.soundSettings);

    // Initialize game state and world
    this.initializeGameState(initialState);

    buildingLogger.info('GameEngine initialized', {
      playerPos: this.state.playerPos,
    });

    // Trigger "First Steps" achievement for new games
    this.triggerAchievementCheck({ isNewGame: true });
  }

  public getTickSystem(): TickSystem {
    return this.tickSystem;
  }

  public getCurrentTick(): number {
    return this.tickSystem.getCurrentTick();
  }

  public resetTickCount(): void {
    this.tickSystem.resetTickCount();
  }

  public enableTestingMode(): void {
    this.tickSystem.enableTestingMode();
  }

  public disableTestingMode(): void {
    this.tickSystem.disableTestingMode();
  }

  public advanceTicks(count: number = 1): void {
    this.tickSystem.advanceTicks(count);
  }

  public getMusicSettings(): MusicSettings {
    return { ...this.musicSettings };
  }

  public setMusicSettings(settings: MusicSettings): MusicSettings {
    const normalized = applyMusicSettingsDefaults(settings);
    this.musicSettings = normalized;
    this.musicEngine.updateSettings(normalized);
    saveMusicSettings(normalized);
    return this.getMusicSettings();
  }

  public updateMusicSettings(updates: Partial<MusicSettings>): MusicSettings {
    return this.setMusicSettings({ ...this.musicSettings, ...updates });
  }

  public startMusic(): void {
    this.musicEngine.ensurePlaying();
  }

  public setAudioSuspended(suspended: boolean): void {
    this.musicEngine.setSuspended(suspended);
    this.soundEngine.setSuspended(suspended);
  }

  public getSoundSettings(): SoundSettings {
    return { ...this.soundSettings };
  }

  public setSoundSettings(settings: SoundSettings): SoundSettings {
    const normalized = applySoundSettingsDefaults(settings);
    this.soundSettings = normalized;
    this.soundEngine.updateSettings(normalized);
    saveSoundSettings(normalized);
    return this.getSoundSettings();
  }

  public updateSoundSettings(updates: Partial<SoundSettings>): SoundSettings {
    return this.setSoundSettings({ ...this.soundSettings, ...updates });
  }

  /**
   * Initialize or reset game state with proper world generation
   * This method is shared between constructor and resetGame
   */
  private initializeGameState(initialState?: Partial<CoreGameState>): void {
    this.lastPowerHistoryRecordTick = null;
    this.powerUptimeStreakTicks = 0;
    this.totalResearchDataProduced = 0;
    this.totalOreExtracted = 0;
    this.totalIronPlatesProduced = 0;

    this.musicEngine.reset();
    this.musicEngine.updateSettings(this.musicSettings);
    this.musicEngine.ensurePlaying();

    this.soundEngine.reset();
    this.soundEngine.updateSettings(this.soundSettings);

    // Load debug menu UI state from localStorage
    const debugMenuUIState = loadDebugMenuUIState();

    const initialHandCraftingQueue = normalizeHandCraftingQueue(
      initialState?.handCraftingQueue ?? []
    );
    const sanitizedInitialState = initialState
      ? { ...initialState, handCraftingQueue: initialHandCraftingQueue }
      : { handCraftingQueue: initialHandCraftingQueue };

    const initialBuildingLayer = sanitizedInitialState.buildingLayer;
    const shouldPlaceStartingGenerators =
      !initialBuildingLayer || Object.keys(initialBuildingLayer).length === 0;

    const {
      itemProductionHistory: initialProductionHistoryRaw,
      productionMenuOpen: initialProductionMenuOpen,
      currentTick: initialCurrentTick,
      ...remainingInitialState
    } = sanitizedInitialState;

    const initialProductionHistory: ItemProductionHistoryEntry[] =
      Array.isArray(initialProductionHistoryRaw)
        ? initialProductionHistoryRaw.map(entry => ({
            tick: entry.tick,
            amounts: { ...entry.amounts },
          }))
        : [];

    // Initialize state with defaults
    this.state = {
      playerPos: {
        x: GAME_CONFIG.WORLD_WIDTH / 2,
        y: GAME_CONFIG.WORLD_HEIGHT / 2,
      },
      playerInventory: createEmptyPlayerInventory(),
      playerFacing: 'down',
      lastMovementTick: 0,
      hoverTargetPosition: null,
      cameraPos: {
        x: GAME_CONFIG.WORLD_WIDTH / 2,
        y: GAME_CONFIG.WORLD_HEIGHT / 2,
      },
      zoomLevel: 2,
      currentBiome: 'Plains',
      tickSpeedMultiplier: 1,
      buildMenuOpen: false,
      selectedCategory: null,
      selectedBuilding: null,
      helpMenuOpen: false,
      saveMenuOpen: false,
      leftPanelOpen: false,
      debugMenuOpen: debugMenuUIState.debugMenuOpen,
      achievementMenuOpen: false,
      researchMenuOpen: false,
      buildingStatusMenuTarget: null,
      resourceNodeMenu: null,
      hasEnabledDebug: false,
      achievements: createInitialAchievementState(),
      achievementSaveData: {},
      prestigePoints: 0,
      prestigeCount: 0,
      quickBeltPreview: null,
      gameMap: {},
      buildingLayer: {},
      beltContentLayer: {},
      assemblerInventoryLayer: {},
      chestInventoryLayer: {},
      rejectedItemsLayer: {},
      minerTimerLayer: {},
      minerOutputBufferLayer: {},
      powerProduction: 0,
      powerDemand: 0,
      powerSatisfaction: 1,
      powerHistory: [
        {
          tick: 0,
          production: 0,
          demand: 0,
          satisfaction: 1,
          demandByBuildingType: {},
        },
      ],
      powerDemandByBuildingType: {},
      researchState: createInitialResearchState(),
      prestigeUpgradeState: createInitialPrestigeUpgradeState(),
      prestigeMenuOpen: false,
      productionMenuOpen: initialProductionMenuOpen ?? false,
      itemProductionHistory: initialProductionHistory,
      currentTick: initialCurrentTick ?? 0,
      ...remainingInitialState,
    };

    this.state.powerHistory = this.state.powerHistory.map(entry => ({
      tick: entry.tick,
      production: entry.production,
      demand: entry.demand,
      satisfaction: entry.satisfaction,
      demandByBuildingType: entry.demandByBuildingType ?? {},
    }));
    this.state.powerDemandByBuildingType =
      this.state.powerDemandByBuildingType ?? {};

    this.state.playerInventory = normalizePlayerInventory(
      this.state.playerInventory
    );
    this.quickBeltAnchor = null;

    const initialInventoryProvided = Boolean(
      initialState?.playerInventory?.some(
        slot => slot?.itemType && slot.count > 0
      )
    );

    if (!initialInventoryProvided) {
      let inventory = this.state.playerInventory;
      for (const { itemType, count } of STARTING_PLAYER_ITEMS) {
        const { inventory: updatedInventory } = addItemToPlayerInventory(
          inventory,
          itemType,
          count
        );
        inventory = updatedInventory;
      }

      this.state.playerInventory = inventory;
    }

    if (shouldPlaceStartingGenerators) {
      const spawnX = Math.floor(GAME_CONFIG.WORLD_WIDTH / 2);
      const spawnY = Math.floor(GAME_CONFIG.WORLD_HEIGHT / 2);
      const generatorCharacter =
        BUILDINGS_BY_ID[BuildingType.GENERATOR].character;
      const generatorPositions = [
        { x: spawnX - 2, y: spawnY + 2 },
        { x: spawnX + 2, y: spawnY + 2 },
      ];

      const updatedBuildingLayer = { ...this.state.buildingLayer };
      let placedGeneratorCount = 0;

      for (const position of generatorPositions) {
        const key = `${position.x},${position.y}`;
        if (updatedBuildingLayer[key]) {
          continue;
        }

        updatedBuildingLayer[key] = generatorCharacter;
        placedGeneratorCount += 1;
      }

      if (placedGeneratorCount > 0) {
        this.state = {
          ...this.state,
          buildingLayer: updatedBuildingLayer,
        };

        buildingLogger.info('Placed starting generators near spawn', {
          count: placedGeneratorCount,
        });
      }
    }

    // Update achievement system configuration
    this.achievementSystem.updateConfig({
      achievementsDisabled: this.state.hasEnabledDebug,
    });

    // Clear movement queue
    this.movementQueue = [];

    // Reset achievement system
    this.achievementSystem.resetAllAchievements();
    this.refreshAchievementProgress();
    this.state.achievementSaveData = this.achievementSystem.getSaveData();

    // Clear all entities
    this.entityManager.clear();
    this.generatedEntityPositions.clear();

    // Initialize collision system with fresh state
    this.initializeCollisionSystem();

    // Generate entities for initial world area around spawn
    const spawnX = Math.floor(GAME_CONFIG.WORLD_WIDTH / 2);
    const spawnY = Math.floor(GAME_CONFIG.WORLD_HEIGHT / 2);
    this.generateEntitiesForArea(spawnX, spawnY, 100);

    this.recalculatePowerNetwork(false);
  }

  /**
   * Initialize collision system with current game state
   */
  private initializeCollisionSystem(): void {
    // Clear existing entities
    this.collisionService.clear();

    // Add all entities from current game state using our getTile function
    const entities = getAllEntitiesFromGameState(
      this.state,
      char => this.findBuildingByCharacter(char),
      (x, y, gameMap, buildingLayer) =>
        this.getTile(x, y, gameMap, buildingLayer)
    );

    entities.forEach(entity => {
      this.collisionService.addEntity(entity);
    });

    buildingLogger.info('Collision system initialized', {
      entityCount: entities.length,
    });

    // Log collision system state for debugging resource node issues
    logCollisionSystemState(this.state, char =>
      this.findBuildingByCharacter(char)
    );
  }

  private recalculatePowerNetwork(recordHistory: boolean): void {
    const countedCenters = new Set<string>();
    let totalProduction = 0;
    let totalDemand = 0;
    const demandBreakdown: PowerDemandByBuildingType = {};
    let activeMachineCount = 0;

    Object.entries(this.state.buildingLayer).forEach(([posKey, char]) => {
      const building = this.findBuildingByCharacter(char);
      if (!building) {
        return;
      }

      let uniqueKey = posKey;
      if (
        building.size &&
        (building.size.width > 1 || building.size.height > 1)
      ) {
        const [xStr, yStr] = posKey.split(',');
        if (xStr && yStr) {
          const x = Number.parseInt(xStr, 10);
          const y = Number.parseInt(yStr, 10);
          if (!Number.isNaN(x) && !Number.isNaN(y)) {
            const center = findBuildingCenter(
              x,
              y,
              building,
              this.state.buildingLayer
            );
            if (center) {
              uniqueKey = `${center.x},${center.y}`;
            }
          }
        }
      }

      if (countedCenters.has(uniqueKey)) {
        return;
      }

      countedCenters.add(uniqueKey);

      if (building.powerGeneration) {
        let generation = building.powerGeneration;

        const variance = building.powerGenerationVariance;
        if (variance) {
          const { amplitude, period } = variance;
          if (amplitude !== 0 && period > 0) {
            const periodTicks = Math.max(secondsToTicks(period), 1);
            const tick = this.getCurrentTick();
            let offset = 0;

            const [centerXStr, centerYStr] = uniqueKey.split(',');
            const centerX = Number.parseInt(centerXStr ?? '', 10);
            const centerY = Number.parseInt(centerYStr ?? '', 10);

            if (!Number.isNaN(centerX) && !Number.isNaN(centerY)) {
              const hash = Math.abs(
                Math.imul(centerX, 73856093) ^ Math.imul(centerY, 19349663)
              );
              offset = hash % periodTicks;
            }

            const phaseTicks = (tick + offset) % periodTicks;
            const phase = (phaseTicks / periodTicks) * Math.PI * 2;
            generation += amplitude * Math.sin(phase);
          }
        }

        if (building.id === BuildingType.GENERATOR) {
          const generatorMultiplier = Math.max(
            1,
            getGeneratorOutputMultiplier(this.state.researchState)
          );
          generation *= generatorMultiplier;
        }

        totalProduction += Math.max(0, generation);
      }

      if (building.powerConsumption) {
        let adjustedConsumption = building.powerConsumption;

        if (building.category === 'mining') {
          const minerMultiplier = Math.max(
            1,
            getMinerSpeedMultiplier(this.state.researchState)
          );
          adjustedConsumption *= minerMultiplier;
        }

        if (
          building.id === BuildingType.ASSEMBLER ||
          building.id === BuildingType.FACTORY
        ) {
          const baseMultiplier =
            building.id === BuildingType.FACTORY
              ? getFactorySpeedMultiplier(this.state.researchState)
              : getAssemblerSpeedMultiplier(this.state.researchState);
          const craftingMultiplier = Math.max(1, baseMultiplier);
          adjustedConsumption *= craftingMultiplier;
        }

        totalDemand += adjustedConsumption;
        const existingConsumption = demandBreakdown[building.id] ?? 0;
        demandBreakdown[building.id] =
          existingConsumption + Math.max(0, adjustedConsumption);

        if (
          adjustedConsumption > 0 &&
          (building.category === 'production' || building.category === 'mining')
        ) {
          activeMachineCount += 1;
        }
      }
    });

    const safeDemand = Math.max(totalDemand, 0);
    const safeProduction = Math.max(totalProduction, 0);

    let adjustedProduction = safeProduction;
    let adjustedSatisfaction =
      safeDemand <= 0 ? 1 : Math.min(safeProduction / safeDemand, 1);

    if (this.unlimitedPowerEnabled) {
      adjustedProduction = Math.max(safeProduction, safeDemand);
      adjustedSatisfaction = 1;
    }

    this.state.powerProduction = adjustedProduction;
    this.state.powerDemand = safeDemand;
    this.state.powerSatisfaction = adjustedSatisfaction;
    this.state.powerDemandByBuildingType = { ...demandBreakdown };

    if (adjustedSatisfaction >= 1 && safeDemand > 0) {
      this.powerUptimeStreakTicks += 1;
    } else {
      this.powerUptimeStreakTicks = 0;
    }

    const powerAchievementContext: AchievementContext = {};
    let shouldTriggerAchievement = false;

    if (
      safeDemand > GameEngine.POWER_HUNGRY_THRESHOLD_MW &&
      !this.isAchievementUnlocked(AchievementType.POWER_HUNGRY)
    ) {
      powerAchievementContext.powerDemand = safeDemand;
      shouldTriggerAchievement = true;
    }

    if (
      activeMachineCount >= GameEngine.FLOW_STATE_MACHINE_THRESHOLD &&
      !this.isAchievementUnlocked(AchievementType.FLOW_STATE)
    ) {
      powerAchievementContext.powerActiveMachines = activeMachineCount;
      shouldTriggerAchievement = true;
    }

    if (
      this.powerUptimeStreakTicks >= GameEngine.POWER_UPTIME_TARGET_TICKS &&
      !this.isAchievementUnlocked(AchievementType.LIGHTS_STAY_ON)
    ) {
      powerAchievementContext.powerUptimeTicks = this.powerUptimeStreakTicks;
      shouldTriggerAchievement = true;
    }

    const snapshot: PowerSnapshot = {
      tick: this.getCurrentTick(),
      production: adjustedProduction,
      demand: safeDemand,
      satisfaction: adjustedSatisfaction,
      demandByBuildingType: { ...demandBreakdown },
    };

    if (shouldTriggerAchievement) {
      this.triggerAchievementCheck(powerAchievementContext, {
        suppressEvent: true,
      });
    }

    if (recordHistory) {
      const lastRecordedTick = this.lastPowerHistoryRecordTick;
      const shouldRecord =
        lastRecordedTick === null ||
        snapshot.tick - lastRecordedTick >=
          GameEngine.POWER_HISTORY_INTERVAL_TICKS;

      if (shouldRecord) {
        const history = [...this.state.powerHistory, snapshot];
        this.state.powerHistory = history.slice(
          Math.max(0, history.length - GameEngine.POWER_HISTORY_LENGTH)
        );
        this.lastPowerHistoryRecordTick = snapshot.tick;
      }
    } else if (this.state.powerHistory.length === 0) {
      this.state.powerHistory = [snapshot];
      this.lastPowerHistoryRecordTick = snapshot.tick;
    } else {
      const history = [...this.state.powerHistory];
      history[history.length - 1] = snapshot;
      this.state.powerHistory = history;
      this.lastPowerHistoryRecordTick = snapshot.tick;
    }
  }

  /**
   * Update collision system when game state changes
   */
  private updateCollisionSystem(): void {
    // For now, we'll do a full rebuild on state changes
    // Later this can be optimized to only update changed entities
    this.initializeCollisionSystem();
  }

  private positionsEqual(a: Position | null, b: Position | null): boolean {
    if (a === b) {
      return true;
    }

    if (!a || !b) {
      return false;
    }

    return a.x === b.x && a.y === b.y;
  }

  // Event system for UI updates
  addEventListener(
    event: GameEngineEvent,
    callback: GameEngineEventCallback
  ): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.add(callback);
    }
  }

  removeEventListener(
    event: GameEngineEvent,
    callback: GameEngineEventCallback
  ): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.delete(callback);
    }
  }

  private emitEvent(event: GameEngineEvent): void {
    const listeners = this.eventListeners.get(event);
    if (!listeners || listeners.size === 0) {
      return;
    }

    this.refreshAchievementProgress();
    listeners.forEach(callback => callback(this.state));
  }

  // State access
  getState(): CoreGameState {
    this.refreshAchievementProgress();
    return { ...this.state }; // Return copy to prevent direct mutation
  }

  getResearchState(): ResearchState {
    return this.state.researchState;
  }

  private getUnlockedResearchCount(): number {
    return Object.values(
      this.state.researchState.unlockedResearch || {}
    ).filter(Boolean).length;
  }

  // Get entity manager
  getEntityManager(): EntityManager {
    return this.entityManager;
  }

  // State mutations - these should be the only way to modify state
  setState(updates: Partial<CoreGameState>): void {
    const buildingLayerChanged = updates.buildingLayer !== undefined;
    let normalizedUpdates =
      updates.playerInventory !== undefined
        ? {
            ...updates,
            playerInventory: normalizePlayerInventory(updates.playerInventory),
          }
        : updates;

    const debugEnabled =
      normalizedUpdates.hasEnabledDebug ?? this.state.hasEnabledDebug;

    if (normalizedUpdates.tickSpeedMultiplier !== undefined) {
      normalizedUpdates = {
        ...normalizedUpdates,
        tickSpeedMultiplier: this.normalizeTickSpeedMultiplier(
          normalizedUpdates.tickSpeedMultiplier,
          debugEnabled
        ),
      };
    }

    if (normalizedUpdates.rejectedItemsLayer !== undefined) {
      normalizedUpdates = {
        ...normalizedUpdates,
        rejectedItemsLayer: this.normalizeRejectedItemsLayer(
          normalizedUpdates.rejectedItemsLayer
        ),
      };
    }

    this.state = { ...this.state, ...normalizedUpdates };

    // Update collision system if world state changed
    if (updates.buildingLayer || updates.gameMap) {
      this.updateCollisionSystem();
    }

    if (buildingLayerChanged) {
      this.recalculatePowerNetwork(false);
    }

    this.emitEvent('stateChanged');
  }

  private normalizeTickSpeedMultiplier(
    multiplier: number,
    hasEnabledDebug: boolean = this.state.hasEnabledDebug
  ): number {
    if (!hasEnabledDebug) {
      return 1;
    }

    if (!Number.isFinite(multiplier)) {
      return 1;
    }

    const integerMultiplier = Math.max(1, Math.floor(multiplier));
    if (GameEngine.TICK_SPEED_OPTIONS.includes(integerMultiplier)) {
      return integerMultiplier;
    }

    // Fall back to closest lower allowed value or default to 1
    let fallback = 1;
    for (const option of GameEngine.TICK_SPEED_OPTIONS) {
      if (integerMultiplier >= option) {
        fallback = option;
      }
    }

    return fallback;
  }

  setTickSpeedMultiplier(multiplier: number): void {
    const normalizedMultiplier = this.normalizeTickSpeedMultiplier(multiplier);
    if (this.state.tickSpeedMultiplier === normalizedMultiplier) {
      return;
    }

    this.state.tickSpeedMultiplier = normalizedMultiplier;
    this.emitEvent('stateChanged');
  }

  cycleTickSpeedMultiplier(): number {
    if (!this.state.hasEnabledDebug) {
      this.setTickSpeedMultiplier(1);
      return 1;
    }

    const currentIndex = GameEngine.TICK_SPEED_OPTIONS.indexOf(
      this.state.tickSpeedMultiplier
    );
    const nextIndex =
      currentIndex === -1
        ? 0
        : (currentIndex + 1) % GameEngine.TICK_SPEED_OPTIONS.length;
    const options = GameEngine.TICK_SPEED_OPTIONS;
    const nextMultiplier = options[nextIndex] ?? options[0] ?? 1;
    this.setTickSpeedMultiplier(nextMultiplier);
    return nextMultiplier;
  }

  setUnlimitedPowerMode(enabled: boolean): void {
    this.unlimitedPowerEnabled = enabled;
    this.recalculatePowerNetwork(false);
  }

  isUnlimitedPowerModeEnabled(): boolean {
    return this.unlimitedPowerEnabled;
  }

  setIgnoreBuildingCosts(enabled: boolean): void {
    this.ignoreBuildingCostsEnabled = enabled;
  }

  isIgnoringBuildingCosts(): boolean {
    return this.ignoreBuildingCostsEnabled;
  }

  unlockResearch(
    researchId: ResearchId,
    options?: { suppressEvent?: boolean }
  ): void {
    const suppressEvent = options?.suppressEvent ?? false;
    const researchDefinition = RESEARCHES[researchId];
    if (!researchDefinition) {
      buildingLogger.warn('Attempted to unlock unknown research', {
        researchId,
      });
      return;
    }

    const prerequisitesSatisfied = researchDefinition.prerequisites.every(
      prerequisite =>
        isResearchUnlockedInState(this.state.researchState, prerequisite)
    );

    if (!prerequisitesSatisfied) {
      buildingLogger.warn(
        'Cannot unlock research, prerequisites not satisfied',
        {
          researchId,
          prerequisites: researchDefinition.prerequisites,
        }
      );
      return;
    }

    const repeatableConfig = getRepeatableResearchConfig(researchId);
    const alreadyUnlocked = isResearchUnlockedInState(
      this.state.researchState,
      researchId
    );

    if (!repeatableConfig && alreadyUnlocked) {
      return;
    }

    if (repeatableConfig) {
      const currentLevel = getRepeatableResearchLevel(
        this.state.researchState,
        researchId
      );
      if (
        repeatableConfig.maxLevel !== undefined &&
        currentLevel >= repeatableConfig.maxLevel
      ) {
        return;
      }
    }

    this.state = {
      ...this.state,
      researchState: unlockResearchId(this.state.researchState, researchId),
    };

    const totalCompleted = this.getUnlockedResearchCount();
    const achievementContext: AchievementContext = {
      researchUnlocked: { researchId },
    };

    if (
      !this.isAchievementUnlocked(AchievementType.APPLIED_ENTROPY) &&
      totalCompleted >= GameEngine.RESEARCH_COMPLETION_TARGET
    ) {
      achievementContext.totalResearchCompleted = totalCompleted;
    }

    this.refreshAchievementProgress();
    this.triggerAchievementCheck(achievementContext, { suppressEvent });

    if (!suppressEvent) {
      this.emitEvent('stateChanged');
    }
  }

  forceUnlockResearch(researchId: ResearchId): void {
    if (!this.state.hasEnabledDebug) {
      this.unlockResearch(researchId);
      return;
    }

    const researchDefinition = RESEARCHES[researchId];
    if (!researchDefinition) {
      buildingLogger.warn('Attempted to force unlock unknown research', {
        researchId,
      });
      return;
    }

    const prerequisitesSatisfied = researchDefinition.prerequisites.every(
      prerequisite =>
        isResearchUnlockedInState(this.state.researchState, prerequisite)
    );

    if (!prerequisitesSatisfied) {
      buildingLogger.info('Force unlocking research without prerequisites', {
        researchId,
        missingPrerequisites: researchDefinition.prerequisites.filter(
          prerequisite =>
            !isResearchUnlockedInState(this.state.researchState, prerequisite)
        ),
      });
    }

    const nextResearchState = unlockResearchId(
      this.state.researchState,
      researchId
    );

    if (nextResearchState === this.state.researchState) {
      return;
    }

    this.state = {
      ...this.state,
      researchState: nextResearchState,
    };

    const totalCompleted = this.getUnlockedResearchCount();
    const achievementContext: AchievementContext = {
      researchUnlocked: { researchId },
    };

    if (
      !this.isAchievementUnlocked(AchievementType.APPLIED_ENTROPY) &&
      totalCompleted >= GameEngine.RESEARCH_COMPLETION_TARGET
    ) {
      achievementContext.totalResearchCompleted = totalCompleted;
    }

    this.refreshAchievementProgress();
    this.triggerAchievementCheck(achievementContext);

    this.emitEvent('stateChanged');
  }

  isResearchUnlocked(researchId: ResearchId): boolean {
    return isResearchUnlockedInState(this.state.researchState, researchId);
  }

  isBuildingUnlocked(buildingType: BuildingType): boolean {
    return isBuildingUnlockedInState(this.state.researchState, buildingType);
  }

  isRecipeUnlocked(recipeId: Recipe['id']): boolean {
    return isRecipeUnlockedInState(this.state.researchState, recipeId);
  }

  selectResearch(researchId: ResearchId | null): void {
    const updatedResearchState = setSelectedResearch(
      this.state.researchState,
      researchId
    );

    if (updatedResearchState === this.state.researchState) {
      return;
    }

    this.state = {
      ...this.state,
      researchState: updatedResearchState,
    };

    this.emitEvent('stateChanged');
  }

  addResearchItems(
    researchId: ResearchId,
    itemType: ItemType,
    amount: number
  ): { accepted: boolean; completed: boolean } {
    const result = addResearchProgress(
      this.state.researchState,
      researchId,
      itemType,
      amount
    );

    if (!result.accepted) {
      return {
        accepted: false,
        completed: false,
      };
    }

    const progressStateChanged = result.state !== this.state.researchState;

    if (progressStateChanged) {
      this.state = {
        ...this.state,
        researchState: result.state,
      };

      if (!result.completed) {
        this.emitEvent('stateChanged');
      }
    }

    if (result.completed) {
      this.unlockResearch(researchId);
      return {
        accepted: true,
        completed: true,
      };
    }

    return {
      accepted: true,
      completed: false,
    };
  }

  // Unified build menu state management
  setBuildMenuState(updates: {
    buildMenuOpen?: BuildMenuState;
    selectedCategory?: BuildingCategory | null;
    selectedBuilding?: Building | null;
  }): void {
    const shouldCloseResourceMenu =
      updates.buildMenuOpen !== undefined && updates.buildMenuOpen !== false;

    const nextState: CoreGameState = {
      ...this.state,
      ...updates,
      ...(shouldCloseResourceMenu ? { resourceNodeMenu: null } : {}),
    };

    const selectionChanged = updates.selectedBuilding !== undefined;
    const buildMenuClosed = updates.buildMenuOpen === false;

    if (selectionChanged || buildMenuClosed) {
      nextState.quickBeltPreview = null;
      this.quickBeltAnchor = null;
    }

    this.state = nextState;
    this.emitEvent('stateChanged');
  }

  setResearchMenuOpen(open: boolean): void {
    this.state = {
      ...this.state,
      researchMenuOpen: open,
      ...(open
        ? {
            helpMenuOpen: false,
            saveMenuOpen: false,
            debugMenuOpen: false,
            achievementMenuOpen: false,
            resourceNodeMenu: null,
            productionMenuOpen: false,
          }
        : {}),
    };

    this.emitEvent('stateChanged');
  }

  setProductionMenuOpen(open: boolean): void {
    this.state = {
      ...this.state,
      productionMenuOpen: open,
      ...(open
        ? {
            helpMenuOpen: false,
            saveMenuOpen: false,
            debugMenuOpen: false,
            achievementMenuOpen: false,
            researchMenuOpen: false,
            prestigeMenuOpen: false,
            resourceNodeMenu: null,
          }
        : {}),
    };

    this.emitEvent('stateChanged');
  }

  // Prestige system methods (stub implementations)
  setPrestigeMenuOpen(open: boolean): void {
    this.state = {
      ...this.state,
      prestigeMenuOpen: open,
      ...(open
        ? {
            helpMenuOpen: false,
            saveMenuOpen: false,
            debugMenuOpen: false,
            achievementMenuOpen: false,
            researchMenuOpen: false,
            productionMenuOpen: false,
            resourceNodeMenu: null,
          }
        : {}),
    };

    this.emitEvent('stateChanged');
  }

  selectPrestigeUpgrade(upgradeId: PrestigeUpgradeId | null): void {
    if (this.state.prestigeUpgradeState.selectedUpgrade === upgradeId) {
      return;
    }

    const nextState = selectPrestigeUpgradeState(
      this.state.prestigeUpgradeState,
      upgradeId
    );

    this.state = {
      ...this.state,
      prestigeUpgradeState: nextState,
    };

    this.emitEvent('stateChanged');
  }

  purchasePrestigeUpgrade(upgradeId: PrestigeUpgradeId): boolean {
    const upgrade = PRESTIGE_UPGRADES[upgradeId];

    if (!upgrade) {
      buildingLogger.warn('Attempted to purchase unknown prestige upgrade', {
        upgradeId,
      });
      return false;
    }

    if (
      isPrestigeUpgradePurchased(this.state.prestigeUpgradeState, upgradeId)
    ) {
      buildingLogger.warn('Attempted to repurchase prestige upgrade', {
        upgradeId,
      });
      return false;
    }

    const currentPrestigePoints = this.state.prestigePoints ?? 0;
    if (currentPrestigePoints < upgrade.cost) {
      buildingLogger.warn('Insufficient prestige points for upgrade purchase', {
        upgradeId,
        prestigePoints: currentPrestigePoints,
        cost: upgrade.cost,
      });
      return false;
    }

    const nextPrestigePoints = currentPrestigePoints - upgrade.cost;
    const nextPrestigeState = purchasePrestigeUpgradeState(
      this.state.prestigeUpgradeState,
      upgradeId
    );

    this.state = {
      ...this.state,
      prestigePoints: nextPrestigePoints,
      prestigeUpgradeState: nextPrestigeState,
    };

    buildingLogger.info('Prestige upgrade purchased', {
      upgradeId,
      cost: upgrade.cost,
      remainingPrestigePoints: nextPrestigePoints,
    });

    this.emitEvent('stateChanged');
    return true;
  }

  // Helper method to check if build menu is in auto-build-on-select mode
  isAutoBuildOnSelect(): boolean {
    return this.state.buildMenuOpen === 'autoBuildOnSelect';
  }

  // Building status menu target management
  setBuildingStatusMenuTarget(target: { x: number; y: number } | null): void {
    this.state = { ...this.state, buildingStatusMenuTarget: target };
    this.emitEvent('stateChanged');
  }

  openBuildingStatusMenu(position: { x: number; y: number }): void {
    this.state = {
      ...this.state,
      buildingStatusMenuTarget: position,
      resourceNodeMenu: null,
    };
    this.emitEvent('stateChanged');
  }

  closeBuildingStatusMenu(): void {
    this.state = {
      ...this.state,
      buildingStatusMenuTarget: null,
    };
    this.emitEvent('stateChanged');
  }

  openResourceNodeMenu(position: Position): void {
    const tile = this.getTile(
      position.x,
      position.y,
      this.state.gameMap,
      this.state.buildingLayer
    );

    const itemType = this.resolveResourceItemType(tile);

    inputLogger.debug('Attempting to open resource node menu', {
      position,
      tile,
      itemType,
    });

    if (!tile || !itemType) {
      inputLogger.debug(
        'openResourceNodeMenu: No valid resource node at target'
      );
      return;
    }

    const menuState: ResourceNodeMenuState = {
      position,
      resourceItemType: itemType,
      resourceCharacter: tile,
    };

    this.state = {
      ...this.state,
      resourceNodeMenu: menuState,
      buildMenuOpen: false,
      selectedCategory: null,
      selectedBuilding: null,
      buildingStatusMenuTarget: null,
    };

    this.emitEvent('stateChanged');

    inputLogger.debug('Opened resource node menu', {
      menuState,
    });
  }

  closeResourceNodeMenu(): void {
    if (!this.state.resourceNodeMenu) {
      return;
    }

    this.state = {
      ...this.state,
      resourceNodeMenu: null,
    };

    this.emitEvent('stateChanged');

    inputLogger.debug('Closed resource node menu');
  }

  mineResourceNode(position: Position): MineResourceResult {
    const tile = this.getTile(
      position.x,
      position.y,
      this.state.gameMap,
      this.state.buildingLayer
    );
    const itemType = this.resolveResourceItemType(tile);

    if (!itemType) {
      inputLogger.debug('mineResourceNode: No resource found at position', {
        position,
        tile,
      });
      return { success: false, reason: 'no-resource' };
    }

    const added = this.addItemToInventory(itemType, 1);

    if (added <= 0) {
      inputLogger.debug(
        'mineResourceNode: Inventory full, could not add item',
        {
          position,
          itemType,
        }
      );
      return { success: false, itemType, reason: 'inventory-full' };
    }

    this.recordItemProduction(itemType, added);

    inputLogger.debug('mineResourceNode: Added resource to inventory', {
      position,
      itemType,
      added,
    });

    return { success: true, itemType, added };
  }

  /**
   * Get collision service for external access (e.g., from UI components)
   */
  getCollisionService(): CollisionService {
    return this.collisionService;
  }

  /**
   * Generate entities for a rectangular area (called during world initialization)
   */
  generateEntitiesForArea(
    centerX: number,
    centerY: number,
    radius: number
  ): void {
    for (let x = centerX - radius; x <= centerX + radius; x++) {
      if (x < 0 || x >= GAME_CONFIG.WORLD_WIDTH) {
        continue;
      }
      for (let y = centerY - radius; y <= centerY + radius; y++) {
        if (y < 0 || y >= GAME_CONFIG.WORLD_HEIGHT) {
          continue;
        }

        const positionKey = `${x},${y}`;
        if (this.generatedEntityPositions.has(positionKey)) {
          continue;
        }

        this.generatedEntityPositions.add(positionKey);

        const entities = generateEntitiesForPosition(x, y, 'wildlife');
        entities.forEach(entity => {
          this.entityManager.addEntity(entity);
        });
      }
    }
  }

  // Player actions
  movePlayer(newX: number, newY: number): void {
    const oldPos = this.state.playerPos;

    const previousHoverTarget = this.state.hoverTargetPosition;
    const hadHoverTarget = previousHoverTarget !== null;

    // Determine facing direction based on movement
    let newFacing = this.state.playerFacing;
    if (newX > oldPos.x) newFacing = 'right';
    else if (newX < oldPos.x) newFacing = 'left';
    else if (newY > oldPos.y) newFacing = 'down';
    else if (newY < oldPos.y) newFacing = 'up';

    // Move player first
    const newPosition = { x: newX, y: newY };
    this.state = {
      ...this.state,
      playerPos: newPosition,
      playerFacing: newFacing,
      cameraPos: newPosition,
      lastMovementTick: this.getCurrentTick(),
      hoverTargetPosition: previousHoverTarget,
    };

    const wildlifeGenerationRadius = 12;
    this.generateEntitiesForArea(
      newPosition.x,
      newPosition.y,
      wildlifeGenerationRadius
    );

    // Now check for entity collisions at the new position
    const entitiesAtPosition = this.entityManager.getEntitiesAt(newPosition);

    // Create a simple player entity for collision purposes
    const playerEntity = {
      id: 'player',
      type: 'player' as EntityType,
      position: newPosition,
      getDisplayChar: () => '🤖',
      getCSSClass: () => 'player',
      getTooltip: () => 'Player',
      isSolid: () => true,
      update: () => {},
      canPlace: () => false,
      serialize: () => ({
        id: 'player',
        type: 'player' as EntityType,
        position: newPosition,
      }),
      isAt: (pos: Position) =>
        pos.x === newPosition.x && pos.y === newPosition.y,
      moveTo: () => {},
      getPositionKey: () => `${newPosition.x},${newPosition.y}`,
      shouldDestroy: () => false,
      collide: () => false,
    };

    for (const entity of entitiesAtPosition) {
      // Let each entity handle the collision with the player
      entity.collide(playerEntity);
    }

    if (hadHoverTarget) {
      this.emitEvent('hoverTargetChanged');
    }

    this.emitEvent('playerMoved');
  }

  // Resource management
  private resolveResourceItemType(tile?: string): ItemType | null {
    switch (tile) {
      case GAME_CONFIG.IRON_NODE_CHARACTER:
        return 'iron-ore';
      case GAME_CONFIG.COPPER_NODE_CHARACTER:
        return 'copper-ore';
      default:
        return null;
    }
  }

  addItemToInventory(
    itemType: ItemType,
    amount: number = 1,
    emitEvent: boolean = true
  ): number {
    if (amount <= 0) {
      return 0;
    }

    const { inventory, added } = addItemToPlayerInventory(
      this.state.playerInventory,
      itemType,
      amount
    );

    if (added > 0) {
      this.state = {
        ...this.state,
        playerInventory: inventory,
      };

      if (emitEvent) {
        this.emitEvent('stateChanged');
      }
    }

    return added;
  }

  collectResource(x: number, y: number): void {
    const key = `${x},${y}`;
    const tile = this.state.gameMap[key];
    const itemType = this.resolveResourceItemType(tile);

    if (itemType) {
      const added = this.addItemToInventory(itemType, 1, false);
      if (added > 0) {
        this.recordItemProduction(itemType, added);
      }
    }

    this.state = {
      ...this.state,
      gameMap: {
        ...this.state.gameMap,
        [key]: GAME_CONFIG.EMPTY_CHARACTER,
      },
    };
    this.emitEvent('stateChanged');
  }

  collectBuildingInventory(
    position: Position,
    itemType?: ItemType
  ): CollectBuildingInventoryResult {
    const addedItems: Partial<Record<ItemType, number>> = {};
    const { building, centerPosition } = getBuildingAtPosition(
      position.x,
      position.y,
      this.state.buildingLayer
    );

    if (!building || !centerPosition) {
      buildingLogger.debug(
        'collectBuildingInventory: invalid building target',
        {
          position,
        }
      );
      return { success: false, addedItems, reason: 'invalid-building' };
    }

    if (building.category === 'mining') {
      return this.collectMinerOutput(centerPosition, building);
    }

    if (
      building.id === BuildingType.ASSEMBLER ||
      building.id === BuildingType.FACTORY
    ) {
      return this.collectAssemblerInventory(centerPosition, itemType);
    }

    if ((building.stackCapacity ?? 0) > 0) {
      return this.collectChestInventory(centerPosition, itemType);
    }

    buildingLogger.debug(
      'collectBuildingInventory: building has no inventory',
      {
        building: building.id,
        position: centerPosition,
      }
    );
    return { success: false, addedItems, reason: 'no-inventory' };
  }

  private getMinerOutputIntervalTicks(building: Building): number {
    const baseIntervalTicks = secondsToTicks(3);
    const globalMultiplier = Math.max(
      0.1,
      getMinerSpeedMultiplier(this.state.researchState)
    );
    const buildingMultiplier = Math.max(
      0.1,
      building.miningSpeedMultiplier ?? 1
    );
    const effectiveMultiplier = Math.max(
      0.1,
      globalMultiplier * buildingMultiplier
    );
    return Math.max(1, Math.round(baseIntervalTicks / effectiveMultiplier));
  }

  private collectMinerOutput(
    position: Position,
    building: Building
  ): CollectBuildingInventoryResult {
    const addedItems: Partial<Record<ItemType, number>> = {};
    const posKey = `${position.x},${position.y}`;
    const resourceTileFromMap = this.state.gameMap[posKey];
    const resourceTile =
      resourceTileFromMap ?? generateTerrainTile(position.x, position.y);
    const outputType = this.getMinerOutputType(resourceTile, building);
    const bufferCapacity = building.miningBufferCapacity ?? 0;
    const hasBuffer = bufferCapacity > 0;
    const outputPerCycle = Math.max(1, building.miningOutputPerCycle ?? 1);

    if (!outputType && !hasBuffer) {
      buildingLogger.debug('collectMinerOutput: no resource output available', {
        position,
        building: building.id,
      });
      return { success: false, addedItems, reason: 'no-items' };
    }

    const currentTick = this.getCurrentTick();
    const lastOutputTick = this.state.minerTimerLayer[posKey];
    const minerOutputIntervalTicks = this.getMinerOutputIntervalTicks(building);

    if (lastOutputTick === undefined || lastOutputTick > currentTick) {
      const updatedTimers = {
        ...this.state.minerTimerLayer,
        [posKey]: currentTick,
      };

      this.state = {
        ...this.state,
        minerTimerLayer: updatedTimers,
      };
      this.emitEvent('stateChanged');

      return { success: false, addedItems, reason: 'not-ready' };
    }

    const nextOutputTick = lastOutputTick + minerOutputIntervalTicks;

    if (!hasBuffer) {
      if (!outputType) {
        return { success: false, addedItems, reason: 'no-items' };
      }

      if (currentTick < nextOutputTick) {
        return { success: false, addedItems, reason: 'not-ready' };
      }

      const { inventory, added } = addItemToPlayerInventory(
        this.state.playerInventory,
        outputType,
        outputPerCycle
      );

      if (added <= 0) {
        return { success: false, addedItems, reason: 'inventory-full' };
      }

      addedItems[outputType] = added;

      const updatedTimers = {
        ...this.state.minerTimerLayer,
        [posKey]: currentTick,
      };

      this.state = {
        ...this.state,
        playerInventory: inventory,
        minerTimerLayer: updatedTimers,
      };
      this.emitEvent('stateChanged');

      buildingLogger.info('Collected miner output manually', {
        position: posKey,
        itemType: outputType,
      });

      return {
        success: true,
        addedItems,
        ...(added < outputPerCycle ? { partial: true } : {}),
      };
    }

    const updatedTimers = { ...this.state.minerTimerLayer };
    const bufferLayer = { ...this.state.minerOutputBufferLayer };
    let bufferedCount = bufferLayer[posKey] ?? 0;

    if (outputType && currentTick >= nextOutputTick) {
      const availableCapacity = Math.max(0, bufferCapacity - bufferedCount);
      const produceCount = Math.min(outputPerCycle, availableCapacity);

      if (produceCount > 0) {
        bufferedCount += produceCount;
        bufferLayer[posKey] = bufferedCount;
        updatedTimers[posKey] = currentTick;
      }
    }

    if (!outputType) {
      this.state = {
        ...this.state,
        minerTimerLayer: updatedTimers,
        minerOutputBufferLayer: bufferLayer,
      };
      this.emitEvent('stateChanged');
      return { success: false, addedItems, reason: 'no-items' };
    }

    const availableToCollect = bufferedCount;

    if (availableToCollect <= 0) {
      if (currentTick < nextOutputTick) {
        return { success: false, addedItems, reason: 'not-ready' };
      }

      this.state = {
        ...this.state,
        minerTimerLayer: updatedTimers,
        minerOutputBufferLayer: bufferLayer,
      };
      this.emitEvent('stateChanged');
      return { success: false, addedItems, reason: 'not-ready' };
    }

    const { inventory, added } = addItemToPlayerInventory(
      this.state.playerInventory,
      outputType,
      availableToCollect
    );

    if (added <= 0) {
      this.state = {
        ...this.state,
        minerTimerLayer: updatedTimers,
        minerOutputBufferLayer: bufferLayer,
      };
      this.emitEvent('stateChanged');
      return { success: false, addedItems, reason: 'inventory-full' };
    }

    const remaining = availableToCollect - added;
    if (remaining > 0) {
      bufferLayer[posKey] = remaining;
    } else {
      delete bufferLayer[posKey];
    }

    addedItems[outputType] = added;

    this.state = {
      ...this.state,
      playerInventory: inventory,
      minerTimerLayer: updatedTimers,
      minerOutputBufferLayer: bufferLayer,
    };
    this.emitEvent('stateChanged');

    buildingLogger.info('Collected miner output manually', {
      position: posKey,
      itemType: outputType,
      bufferedRemaining: remaining,
    });

    return {
      success: true,
      addedItems,
      ...(added < availableToCollect ? { partial: true } : {}),
    };
  }

  private collectAssemblerInventory(
    position: Position,
    itemType?: ItemType
  ): CollectBuildingInventoryResult {
    const addedItems: Partial<Record<ItemType, number>> = {};
    const posKey = `${position.x},${position.y}`;
    const assemblerInventory = this.state.assemblerInventoryLayer[posKey];

    if (!assemblerInventory) {
      buildingLogger.debug('collectAssemblerInventory: no inventory found', {
        position: posKey,
      });
      return { success: false, addedItems, reason: 'no-inventory' };
    }

    const typesToProcess = itemType ? [itemType] : ITEM_TYPES;
    const updatedItems = { ...assemblerInventory.items };
    let playerInventory = this.state.playerInventory;
    let totalRequested = 0;
    let totalAdded = 0;
    let partial = false;

    typesToProcess.forEach(type => {
      const available = updatedItems[type] ?? 0;
      if (available <= 0) {
        return;
      }

      totalRequested += available;
      const { inventory, added } = addItemToPlayerInventory(
        playerInventory,
        type,
        available
      );

      if (added > 0) {
        playerInventory = inventory;
        updatedItems[type] = Math.max(0, available - added);
        addedItems[type] = (addedItems[type] ?? 0) + added;
        totalAdded += added;
      }

      if (added < available) {
        partial = true;
      }
    });

    if (totalRequested === 0) {
      return { success: false, addedItems, reason: 'no-items' };
    }

    if (totalAdded === 0) {
      return {
        success: false,
        addedItems,
        reason: partial ? 'inventory-full' : 'no-items',
      };
    }

    const normalizedItems = { ...updatedItems };
    ITEM_TYPES.forEach(type => {
      const value = normalizedItems[type] ?? 0;
      normalizedItems[type] = value > 0 ? value : 0;
    });

    const updatedAssemblerInventory: AssemblerInventory = {
      ...assemblerInventory,
      items: normalizedItems,
    };

    this.state = {
      ...this.state,
      playerInventory,
      assemblerInventoryLayer: {
        ...this.state.assemblerInventoryLayer,
        [posKey]: updatedAssemblerInventory,
      },
    };
    this.emitEvent('stateChanged');

    buildingLogger.info('Collected assembler inventory manually', {
      position: posKey,
      items: addedItems,
      partial,
    });

    return {
      success: true,
      addedItems,
      partial,
      ...(partial ? { reason: 'inventory-full' as const } : {}),
    };
  }

  private collectChestInventory(
    position: Position,
    itemType?: ItemType
  ): CollectBuildingInventoryResult {
    const addedItems: Partial<Record<ItemType, number>> = {};
    const posKey = `${position.x},${position.y}`;
    const existingInventory =
      this.state.chestInventoryLayer[posKey] || createEmptyChestInventory();
    const updatedItems = { ...existingInventory.items };
    const typesToProcess = itemType ? [itemType] : ITEM_TYPES;
    let playerInventory = this.state.playerInventory;
    let totalRequested = 0;
    let totalAdded = 0;
    let partial = false;

    typesToProcess.forEach(type => {
      const available = updatedItems[type] ?? 0;
      if (available <= 0) {
        return;
      }

      totalRequested += available;
      const { inventory, added } = addItemToPlayerInventory(
        playerInventory,
        type,
        available
      );

      if (added > 0) {
        playerInventory = inventory;
        updatedItems[type] = Math.max(0, available - added);
        addedItems[type] = (addedItems[type] ?? 0) + added;
        totalAdded += added;
      }

      if (added < available) {
        partial = true;
      }
    });

    if (totalRequested === 0) {
      return { success: false, addedItems, reason: 'no-items' };
    }

    if (totalAdded === 0) {
      return {
        success: false,
        addedItems,
        reason: partial ? 'inventory-full' : 'no-items',
      };
    }

    const normalizedItems = { ...updatedItems };
    ITEM_TYPES.forEach(type => {
      const value = normalizedItems[type] ?? 0;
      normalizedItems[type] = value > 0 ? value : 0;
    });

    const updatedInventory: ChestInventory = {
      items: normalizedItems,
    };

    this.state = {
      ...this.state,
      playerInventory,
      chestInventoryLayer: {
        ...this.state.chestInventoryLayer,
        [posKey]: updatedInventory,
      },
    };
    this.emitEvent('stateChanged');

    buildingLogger.info('Collected chest inventory manually', {
      position: posKey,
      items: addedItems,
      partial,
    });

    return {
      success: true,
      addedItems,
      partial,
      ...(partial ? { reason: 'inventory-full' as const } : {}),
    };
  }

  // Building management
  updateTile(x: number, y: number, character: string): void {
    const key = `${x},${y}`;
    this.state = {
      ...this.state,
      gameMap: {
        ...this.state.gameMap,
        [key]: character,
      },
    };
    this.emitEvent('stateChanged');
  }

  private getHandCraftingLogDetails(
    logContext: HandCraftingLogContext
  ): Record<string, unknown> {
    const details: Record<string, unknown> = {
      source: logContext.source,
    };

    if (logContext.buildingId) {
      details.building = logContext.buildingId;
      details.buildingId = logContext.buildingId;
    }

    if (logContext.item) {
      details.item = logContext.item;
    }

    if (typeof logContext.amount === 'number') {
      details.amount = logContext.amount;
    }

    return details;
  }

  private prepareHandCraftingPlan(
    requirements: readonly BuildingItemRequirement[],
    options?: {
      ignoreInventoryForOutputs?: ItemType[];
      logContext?: HandCraftingLogContext;
    }
  ): PreparedHandCraftingPlan | null {
    if (requirements.length === 0) {
      return null;
    }

    const playerCounts = getPlayerInventoryItemCounts(
      this.state.playerInventory
    );
    const queuedOutputs = calculateHandCraftingQueuedOutputs(
      this.state.handCraftingQueue
    );

    const adjustedPlayerCounts: Record<ItemType, number> = {
      ...playerCounts,
    };
    const adjustedQueuedOutputs: Record<ItemType, number> = {
      ...queuedOutputs,
    };

    if (options?.ignoreInventoryForOutputs) {
      for (const item of options.ignoreInventoryForOutputs) {
        adjustedPlayerCounts[item] = 0;
        adjustedQueuedOutputs[item] = 0;
      }
    }

    const plan = planHandCraftingForRequirements(
      requirements,
      adjustedPlayerCounts,
      adjustedQueuedOutputs
    );

    if (!plan) {
      return null;
    }

    const craftEntries = Object.entries(plan.craftCounts || {})
      .map(([recipeId, count]) => ({ recipeId, count: Number(count) }))
      .filter(entry => entry.count > 0);

    if (craftEntries.length === 0) {
      return null;
    }

    const preparedCrafts: { recipe: Recipe; crafts: number }[] = [];

    for (const { recipeId, count } of craftEntries) {
      const recipe = RECIPES_BY_ID[recipeId as Recipe['id']];
      if (!recipe) {
        const logDetails = options?.logContext
          ? this.getHandCraftingLogDetails(options.logContext)
          : {};
        buildingLogger.warn('Unknown recipe for manual crafting plan', {
          recipeId,
          ...logDetails,
        });
        return null;
      }

      const crafts = Math.max(0, Math.floor(count));
      if (crafts === 0) {
        continue;
      }

      if (!this.isRecipeUnlocked(recipe.id)) {
        const logDetails = options?.logContext
          ? this.getHandCraftingLogDetails(options.logContext)
          : {};
        buildingLogger.warn('Manual crafting blocked by research lock', {
          recipe: recipe.id,
          ...logDetails,
        });
        return null;
      }

      preparedCrafts.push({ recipe, crafts });
    }

    if (preparedCrafts.length === 0) {
      return null;
    }

    const consumptionEntries: ReadonlyArray<readonly [ItemType, number]> =
      Object.entries(plan.consumption ?? {}).flatMap(([item, amount]) => {
        if (typeof amount !== 'number' || amount <= 0) {
          return [];
        }
        return [[item as ItemType, amount] as const];
      });

    return {
      playerCounts,
      consumptionEntries,
      preparedCrafts,
    };
  }

  private applyHandCraftingPlan(
    plan: PreparedHandCraftingPlan,
    logContext: HandCraftingLogContext
  ): boolean {
    const { playerCounts, consumptionEntries, preparedCrafts } = plan;

    if (preparedCrafts.length === 0) {
      return false;
    }

    for (const [item, amount] of consumptionEntries) {
      const available = playerCounts[item] ?? 0;
      if (available < amount) {
        buildingLogger.warn(
          'Failed to reserve manual crafting inputs - insufficient inventory',
          {
            item,
            required: amount,
            available,
            ...this.getHandCraftingLogDetails(logContext),
          }
        );
        return false;
      }
    }

    let updatedInventory = this.state.playerInventory;

    for (const [item, amount] of consumptionEntries) {
      const { inventory, removed } = removeItemsFromPlayerInventory(
        updatedInventory,
        item,
        amount
      );
      if (removed < amount) {
        buildingLogger.warn('Failed to reserve manual crafting inputs', {
          item,
          required: amount,
          removed,
          ...this.getHandCraftingLogDetails(logContext),
        });
        return false;
      }
      updatedInventory = inventory;
    }

    let updatedQueue = [...this.state.handCraftingQueue];
    let idCounter = 0;

    for (const { recipe, crafts } of preparedCrafts) {
      if (crafts <= 0) {
        continue;
      }

      const ticksPerCraft = getHandCraftingTicksPerCraft(recipe);

      const newEntry: HandCraftingQueueEntry = {
        id: `hand_${Date.now()}_${idCounter++}_${Math.random()
          .toString(36)
          .slice(2, 8)}`,
        recipeId: recipe.id,
        recipeName: recipe.name,
        totalCount: crafts,
        remainingCount: crafts,
        ticksPerCraft,
        progressTicks: 0,
      };

      if (updatedQueue.length > 0) {
        const lastEntry = updatedQueue[updatedQueue.length - 1];
        if (lastEntry && lastEntry.recipeId === newEntry.recipeId) {
          const mergedEntry: HandCraftingQueueEntry = {
            ...lastEntry,
            totalCount: lastEntry.totalCount + newEntry.totalCount,
            remainingCount: lastEntry.remainingCount + newEntry.remainingCount,
          };
          updatedQueue = [
            ...updatedQueue.slice(0, updatedQueue.length - 1),
            mergedEntry,
          ];
          continue;
        }
      }

      updatedQueue = [...updatedQueue, newEntry];
    }

    this.state = {
      ...this.state,
      playerInventory: updatedInventory,
      handCraftingQueue: updatedQueue,
    };

    buildingLogger.info('Queued manual hand crafting tasks', {
      ...this.getHandCraftingLogDetails(logContext),
      queuedRecipes: preparedCrafts.map(entry => ({
        recipeId: entry.recipe.id,
        crafts: entry.crafts,
      })),
    });

    this.emitEvent('stateChanged');
    return true;
  }

  private tryQueueHandCrafting(
    building: Building,
    missingRequirements: readonly BuildingItemRequirement[]
  ): boolean {
    if (missingRequirements.length === 0) {
      return false;
    }

    const plan = this.prepareHandCraftingPlan(missingRequirements, {
      logContext: { source: 'building', buildingId: building.id },
    });

    if (!plan) {
      return false;
    }

    return this.applyHandCraftingPlan(plan, {
      source: 'building',
      buildingId: building.id,
    });
  }

  canQueueHandCraftingForBuilding(
    building: Building,
    missingRequirements: readonly BuildingItemRequirement[]
  ): boolean {
    if (missingRequirements.length === 0) {
      return false;
    }

    const plan = this.prepareHandCraftingPlan(missingRequirements, {
      logContext: { source: 'building', buildingId: building.id },
    });

    return plan !== null;
  }

  queueHandCraftingForBuilding(
    building: Building,
    missingRequirements: readonly BuildingItemRequirement[]
  ): boolean {
    if (missingRequirements.length === 0) {
      return false;
    }

    return this.tryQueueHandCrafting(building, missingRequirements);
  }

  queueHandCrafting(item: ItemType, amount: number): boolean {
    const sanitizedAmount = Number.isFinite(amount) ? Math.floor(amount) : 0;
    const normalizedAmount = Math.max(1, sanitizedAmount);

    const plan = this.prepareHandCraftingPlan(
      [
        {
          item,
          amount: normalizedAmount,
        },
      ],
      {
        ignoreInventoryForOutputs: [item],
        logContext: { source: 'manual', item, amount: normalizedAmount },
      }
    );

    if (!plan) {
      return false;
    }

    return this.applyHandCraftingPlan(plan, {
      source: 'manual',
      item,
      amount: normalizedAmount,
    });
  }

  private clearQuickBeltPreviewState(): void {
    if (!this.state.quickBeltPreview && !this.quickBeltAnchor) {
      return;
    }

    this.quickBeltAnchor = null;

    if (this.state.quickBeltPreview) {
      this.setState({ quickBeltPreview: null });
    }
  }

  private clonePosition(position: Position): Position {
    return { x: position.x, y: position.y };
  }

  private aggregateQuickBeltRequirements(
    requirements: readonly BuildingItemRequirement[],
    count: number
  ): BuildingItemRequirement[] {
    if (count <= 0 || requirements.length === 0) {
      return [];
    }

    const totals = new Map<ItemType, number>();
    for (const requirement of requirements) {
      const existing = totals.get(requirement.item) ?? 0;
      totals.set(requirement.item, existing + requirement.amount * count);
    }

    return Array.from(totals.entries()).map(([item, amount]) => ({
      item,
      amount,
    }));
  }

  private buildQuickBeltSegmentsFromPositions(
    positions: Position[],
    fallbackDirection: PlayerFacing = this.state.playerFacing
  ): QuickBeltPreviewSegment[] {
    if (positions.length === 0) {
      return [];
    }

    const segments: QuickBeltPreviewSegment[] = [];

    for (let index = 0; index < positions.length; index += 1) {
      const current = positions[index];

      if (!current) {
        continue;
      }

      const next = positions[index + 1];
      const previous = positions[index - 1];

      let direction: PlayerFacing = fallbackDirection;

      if (next) {
        const toNext = getDirectionBetweenPositions(current, next);
        if (toNext) {
          direction = toNext;
        }
      } else if (previous) {
        const fromPrevious = getDirectionBetweenPositions(previous, current);
        if (fromPrevious) {
          direction = fromPrevious;
        }
      }

      segments.push({
        position: this.clonePosition(current),
        direction,
      });
    }

    return segments;
  }

  private interpolatePositions(from: Position, to: Position): Position[] {
    const path: Position[] = [];

    let currentX = from.x;
    let currentY = from.y;

    const stepX = Math.sign(to.x - from.x);
    const stepY = Math.sign(to.y - from.y);

    while (currentX !== to.x && stepX !== 0) {
      currentX += stepX;
      path.push({ x: currentX, y: currentY });
    }

    while (currentY !== to.y && stepY !== 0) {
      currentY += stepY;
      path.push({ x: currentX, y: currentY });
    }

    if (path.length === 0 && !this.positionsEqual(from, to)) {
      path.push({ x: to.x, y: to.y });
    }

    return path;
  }

  private evaluateQuickBeltPreview(
    beltType: BeltType,
    positions: Position[],
    status: QuickBeltPreviewStatus
  ): QuickBeltPreviewState {
    const beltDefinition = BUILDINGS_BY_ID[beltType];

    if (!beltDefinition) {
      return {
        beltType,
        segments: [],
        status,
        cost: [],
        isValid: false,
        invalidReason: 'Unknown belt type selected',
      };
    }

    const normalizedPositions = positions.map(position =>
      this.clonePosition(position)
    );
    const segments = this.buildQuickBeltSegmentsFromPositions(
      normalizedPositions,
      this.state.playerFacing
    );
    const cost = this.aggregateQuickBeltRequirements(
      beltDefinition.cost.items ?? [],
      segments.length
    );

    let invalidReason: string | undefined;

    if (segments.length === 0) {
      invalidReason = 'Drag across tiles to plan a belt path';
    } else {
      for (const segment of segments) {
        const key = `${segment.position.x},${segment.position.y}`;
        if (this.state.buildingLayer[key]) {
          invalidReason = 'A building already occupies part of this path';
          break;
        }

        const validation = this.collisionService.validateBuildingPlacement(
          beltDefinition,
          segment.position.x,
          segment.position.y
        );

        if (!validation.isValid) {
          invalidReason = 'Cannot place a belt on one of the selected tiles';
          break;
        }
      }
    }

    return {
      beltType,
      segments,
      status,
      cost,
      isValid: invalidReason === undefined,
      ...(invalidReason ? { invalidReason } : {}),
    };
  }

  startQuickBeltPlacement(beltType: BeltType, startPosition: Position): void {
    this.quickBeltAnchor = this.clonePosition(startPosition);
    const preview = this.evaluateQuickBeltPreview(
      beltType,
      [startPosition],
      'dragging'
    );
    this.setState({ quickBeltPreview: preview });
  }

  extendQuickBeltPlacement(position: Position): void {
    const preview = this.state.quickBeltPreview;
    if (!preview || preview.status !== 'dragging') {
      return;
    }

    const positions = preview.segments.map(segment =>
      this.clonePosition(segment.position)
    );
    const lastPosition = positions[positions.length - 1];

    if (!lastPosition) {
      return;
    }

    if (this.positionsEqual(lastPosition, position)) {
      return;
    }

    const existingIndex = positions.findIndex(pos =>
      this.positionsEqual(pos, position)
    );

    let nextPositions: Position[];

    if (existingIndex >= 0) {
      nextPositions = positions.slice(0, existingIndex + 1);
    } else {
      const interpolated = this.interpolatePositions(lastPosition, position);
      if (interpolated.length === 0) {
        return;
      }
      nextPositions = [...positions, ...interpolated];
    }

    const nextPreview = this.evaluateQuickBeltPreview(
      preview.beltType,
      nextPositions,
      'dragging'
    );

    this.setState({ quickBeltPreview: nextPreview });
  }

  finalizeQuickBeltPreview(): void {
    const preview = this.state.quickBeltPreview;
    this.quickBeltAnchor = null;

    if (!preview) {
      return;
    }

    const positions = preview.segments.map(segment =>
      this.clonePosition(segment.position)
    );
    const finalizedPreview = this.evaluateQuickBeltPreview(
      preview.beltType,
      positions,
      'confirm'
    );

    this.setState({ quickBeltPreview: finalizedPreview });
  }

  cancelQuickBeltPlacement(): void {
    this.clearQuickBeltPreviewState();
  }

  confirmQuickBeltPlacement(): QuickBeltPlacementResult {
    const preview = this.state.quickBeltPreview;

    if (!preview) {
      return { success: false, error: 'No belt preview to confirm' };
    }

    if (!preview.isValid) {
      return {
        success: false,
        error:
          preview.invalidReason ??
          'The planned belt path is not currently valid',
      };
    }

    if (preview.segments.length === 0) {
      this.clearQuickBeltPreviewState();
      return { success: false, error: 'Select at least one tile for the belt' };
    }

    const beltDefinition = BUILDINGS_BY_ID[preview.beltType];

    if (!beltDefinition) {
      this.clearQuickBeltPreviewState();
      return { success: false, error: 'Unknown belt type selected' };
    }

    for (const segment of preview.segments) {
      const key = `${segment.position.x},${segment.position.y}`;
      if (this.state.buildingLayer[key]) {
        return {
          success: false,
          error: 'Another building now blocks the planned belt path',
        };
      }

      const validation = this.collisionService.validateBuildingPlacement(
        beltDefinition,
        segment.position.x,
        segment.position.y
      );

      if (!validation.isValid) {
        return {
          success: false,
          error: 'Cannot place a belt on one of the selected tiles',
        };
      }
    }

    let updatedPlayerInventory = this.state.playerInventory;
    let updatedChestInventoryLayer = this.state.chestInventoryLayer;
    let playerInventoryChanged = false;
    let chestInventoryChanged = false;

    if (!this.ignoreBuildingCostsEnabled && preview.cost.length > 0) {
      const availableItems = getAvailableItemCountsForPlacement(
        this.state.playerInventory,
        this.state.chestInventoryLayer
      );

      const missingRequirement = preview.cost.find(
        ({ item, amount }) => (availableItems[item] ?? 0) < amount
      );

      if (missingRequirement) {
        return {
          success: false,
          error: 'Not enough items are available to place these belts',
        };
      }

      const consumptionResult = consumeItemsForBuilding(
        this.state.playerInventory,
        this.state.chestInventoryLayer,
        preview.cost
      );

      if (!consumptionResult.success) {
        return {
          success: false,
          error: 'Unable to reserve the required items for belt placement',
        };
      }

      updatedPlayerInventory = consumptionResult.playerInventory;
      updatedChestInventoryLayer = consumptionResult.chestInventoryLayer;
      playerInventoryChanged = consumptionResult.playerInventoryChanged;
      chestInventoryChanged = consumptionResult.chestInventoryChanged;
    }

    let updatedBuildingLayer = { ...this.state.buildingLayer };

    for (const segment of preview.segments) {
      const smartResult = processSmartBeltPlacement(
        segment.position,
        preview.beltType,
        segment.direction,
        updatedBuildingLayer
      );

      const key = `${segment.position.x},${segment.position.y}`;
      const finalCharacter = getDirectionalBuildingCharacter(
        beltDefinition,
        segment.direction
      );

      updatedBuildingLayer = {
        ...smartResult.updatedBuildingLayer,
        [key]: finalCharacter,
      };

      recordBeltPlacement(
        segment.position,
        preview.beltType,
        segment.direction
      );
    }

    const updates: Partial<CoreGameState> = {
      buildingLayer: updatedBuildingLayer,
      quickBeltPreview: null,
    };

    if (playerInventoryChanged) {
      updates.playerInventory = updatedPlayerInventory;
    }

    if (chestInventoryChanged) {
      updates.chestInventoryLayer = updatedChestInventoryLayer;
    }

    this.quickBeltAnchor = null;
    this.setState(updates);

    buildingLogger.info('Quick belt placement completed', {
      beltType: preview.beltType,
      tiles: preview.segments.length,
    });

    return { success: true, placedCount: preview.segments.length };
  }

  // Building placement with collision validation
  placeBuilding(x: number, y: number, building: Building): boolean {
    const key = `${x},${y}`;

    if (!this.isBuildingUnlocked(building.id)) {
      buildingLogger.warn('Building placement failed: research locked', {
        building: building.id,
      });
      return false;
    }

    const itemRequirements = building.cost.items ?? [];
    let updatedChestInventoryLayer = this.state.chestInventoryLayer;
    let updatedPlayerInventory = this.state.playerInventory;

    if (itemRequirements.length > 0) {
      const availableItems = getAvailableItemCountsForPlacement(
        this.state.playerInventory,
        this.state.chestInventoryLayer
      );
      const queuedOutputs = calculateHandCraftingQueuedOutputs(
        this.state.handCraftingQueue
      );

      const missingRequirements = itemRequirements
        .map(requirement => {
          const effectiveAvailable =
            (availableItems[requirement.item] ?? 0) +
            (queuedOutputs[requirement.item] ?? 0);
          const missingAmount = requirement.amount - effectiveAvailable;
          if (missingAmount > 0) {
            return {
              item: requirement.item,
              amount: missingAmount,
            };
          }
          return null;
        })
        .filter((req): req is BuildingItemRequirement => req !== null);

      if (missingRequirements.length > 0) {
        const queuedHandCrafting = this.tryQueueHandCrafting(
          building,
          missingRequirements
        );

        if (!queuedHandCrafting) {
          buildingLogger.debug(
            'Building placement failed: insufficient item requirements',
            {
              building: building.id,
              missing: missingRequirements,
            }
          );
        }

        return false;
      }

      const consumptionResult = consumeItemsForBuilding(
        this.state.playerInventory,
        this.state.chestInventoryLayer,
        itemRequirements
      );

      if (!consumptionResult.success) {
        buildingLogger.warn(
          'Building placement failed: unable to reserve item requirements',
          {
            building: building.id,
            requirements: itemRequirements,
          }
        );
        return false;
      }

      updatedChestInventoryLayer = consumptionResult.chestInventoryLayer;
      updatedPlayerInventory = consumptionResult.playerInventory;
    }

    // Use collision system for placement validation
    const validationResult = this.collisionService.validateBuildingPlacement(
      building,
      x,
      y
    );

    if (!validationResult.isValid) {
      buildingLogger.debug('Building placement failed: collision validation', {
        building: building.id,
        position: { x, y },
        errors: validationResult.errors,
        warnings: validationResult.warnings,
        blockingEntities: validationResult.blockingEntities.map(e => ({
          id: e.id,
          type: e.type,
        })),
      });

      // If it's a miner placement failure, log detailed resource node collision issues
      if (building.category === 'mining') {
        logResourceNodeCollisionIssues(
          this.state,
          this.collisionService,
          char => this.findBuildingByCharacter(char)
        );
        logMinerPlacementCollisionIssues(
          this.collisionService,
          this.state,
          char => this.findBuildingByCharacter(char)
        );
      }

      return false;
    }

    // Check if this is a belt for smart placement
    const isBelt =
      building.id === BuildingType.BELT ||
      building.id === BuildingType.FAST_BELT;

    if (isBelt) {
      // Handle smart belt placement
      const smartBeltResult = processSmartBeltPlacement(
        { x, y },
        building.id as BeltType,
        this.state.playerFacing,
        this.state.buildingLayer
      );

      // Use optimized direction if smart belt system suggests one
      const finalDirection =
        smartBeltResult.optimizedNewDirection || this.state.playerFacing;
      const finalBuildingCharacter = getDirectionalBuildingCharacter(
        building,
        finalDirection
      );

      // Update building layer with smart belt changes and add the new belt
      this.state = {
        ...this.state,
        buildingLayer: {
          ...smartBeltResult.updatedBuildingLayer,
          [key]: finalBuildingCharacter,
        },
        chestInventoryLayer: updatedChestInventoryLayer,
        playerInventory: updatedPlayerInventory,
      };

      // Record the belt placement for future smart connections
      recordBeltPlacement({ x, y }, building.id as BeltType, finalDirection);

      // Log smart belt connection if it happened
      if (
        smartBeltResult.previousBeltUpdated &&
        smartBeltResult.previousBeltPosition
      ) {
        buildingLogger.info('Smart belt connection made', {
          newBelt: { x, y, direction: finalDirection },
          previousBelt: {
            position: smartBeltResult.previousBeltPosition,
            oldDirection: smartBeltResult.previousBeltOldDirection,
            newDirection: smartBeltResult.previousBeltNewDirection,
          },
          optimizedDirection: smartBeltResult.optimizedNewDirection
            ? 'used'
            : 'not_needed',
        });
      }
    } else {
      // Place non-belt building with directional character support
      const buildingCharacter = getDirectionalBuildingCharacter(
        building,
        this.state.playerFacing
      );

      this.state = {
        ...this.state,
        buildingLayer: {
          ...this.state.buildingLayer,
          [key]: buildingCharacter,
        },
        chestInventoryLayer: updatedChestInventoryLayer,
        playerInventory: updatedPlayerInventory,
      };
    }

    // Placement modifies the world - rebuild collision data so future validations see the update
    this.updateCollisionSystem();

    this.recalculatePowerNetwork(false);

    // Initialize building-specific state
    if (building.category === 'mining') {
      this.state.minerTimerLayer = {
        ...this.state.minerTimerLayer,
        [key]: this.getCurrentTick(),
      };

      if ((building.miningBufferCapacity ?? 0) > 0) {
        this.state.minerOutputBufferLayer = {
          ...this.state.minerOutputBufferLayer,
          [key]: 0,
        };
      }
    }

    if (
      building.id === BuildingType.ASSEMBLER ||
      building.id === BuildingType.FACTORY
    ) {
      this.state.assemblerInventoryLayer = {
        ...this.state.assemblerInventoryLayer,
        [key]: {
          items: createEmptyItemCounts(),
          recipe: null,
          craftingProgress: 0,
          craftingStartTick: null,
        },
      };
    }

    if (building.stackCapacity) {
      this.state.chestInventoryLayer = {
        ...this.state.chestInventoryLayer,
        [key]: createEmptyChestInventory(),
      };
    }
    // Trigger building placement achievements (state change emitted below)
    this.triggerAchievementCheck(
      {
        buildingPlaced: {
          buildingType: building.id,
          position: { x, y },
        },
      },
      { suppressEvent: true }
    );

    this.emitEvent('buildingPlaced');
    this.emitEvent('stateChanged');
    return true;
  }

  upgradeAssemblerToFactory(position: Position): UpgradeAssemblerResult {
    const { building, centerPosition } = getBuildingAtPosition(
      position.x,
      position.y,
      this.state.buildingLayer
    );

    if (
      !building ||
      building.id !== BuildingType.ASSEMBLER ||
      !centerPosition
    ) {
      buildingLogger.debug(
        'Assembler upgrade failed: no assembler at position',
        {
          position,
        }
      );
      return { success: false, reason: 'not-assembler' };
    }

    if (!this.isBuildingUnlocked(BuildingType.FACTORY)) {
      buildingLogger.debug(
        'Assembler upgrade failed: factory research locked',
        {
          position,
        }
      );
      return { success: false, reason: 'factory-locked' };
    }

    const factoryDefinition = BUILDINGS.FACTORY;
    const upgradeCost = factoryDefinition.cost.items ?? [];

    let nextChestInventoryLayer = this.state.chestInventoryLayer;
    let nextPlayerInventory = this.state.playerInventory;

    if (!this.ignoreBuildingCostsEnabled && upgradeCost.length > 0) {
      const availableItems = getAvailableItemCountsForPlacement(
        this.state.playerInventory,
        this.state.chestInventoryLayer
      );

      const missingRequirement = upgradeCost.find(
        ({ item, amount }) => (availableItems[item] ?? 0) < amount
      );

      if (missingRequirement) {
        buildingLogger.debug('Assembler upgrade failed: insufficient items', {
          position,
          missingRequirement,
        });
        return { success: false, reason: 'insufficient-items' };
      }

      const consumptionResult = consumeItemsForBuilding(
        this.state.playerInventory,
        this.state.chestInventoryLayer,
        upgradeCost
      );

      if (!consumptionResult.success) {
        buildingLogger.warn(
          'Assembler upgrade failed: unable to consume upgrade cost',
          {
            position,
            upgradeCost,
          }
        );
        return { success: false, reason: 'consumption-failed' };
      }

      nextChestInventoryLayer = consumptionResult.chestInventoryLayer;
      nextPlayerInventory = consumptionResult.playerInventory;
    } else if (this.ignoreBuildingCostsEnabled) {
      buildingLogger.info('Assembler upgrade cost ignored via debug setting', {
        position,
      });
    }

    const assemblerDefinition = BUILDINGS.ASSEMBLER;
    const refundItems = assemblerDefinition.cost.items ?? [];
    const refundedItems: Partial<Record<ItemType, number>> = {};

    for (const requirement of refundItems) {
      const { inventory, added } = addItemToPlayerInventory(
        nextPlayerInventory,
        requirement.item,
        requirement.amount
      );

      nextPlayerInventory = inventory;

      if (added > 0) {
        refundedItems[requirement.item] =
          (refundedItems[requirement.item] ?? 0) + added;
      }

      if (added < requirement.amount) {
        buildingLogger.warn('Assembler upgrade refund partially failed', {
          position,
          itemType: requirement.item,
          attempted: requirement.amount,
          refunded: added,
        });
      }
    }

    const factoryCharacter = factoryDefinition.character;
    const centerKey = `${centerPosition.x},${centerPosition.y}`;
    const newBuildingLayer = {
      ...this.state.buildingLayer,
      [centerKey]: factoryCharacter,
    };

    this.state = {
      ...this.state,
      buildingLayer: newBuildingLayer,
      playerInventory: nextPlayerInventory,
      chestInventoryLayer: nextChestInventoryLayer,
    };

    this.updateCollisionSystem();
    this.recalculatePowerNetwork(false);

    if (Object.keys(refundedItems).length > 0) {
      buildingLogger.info('Assembler upgraded to factory', {
        position: `${centerPosition.x},${centerPosition.y}`,
        refundedItems,
        upgradeCost,
      });
    } else {
      buildingLogger.info('Assembler upgraded to factory', {
        position: `${centerPosition.x},${centerPosition.y}`,
        upgradeCost,
      });
    }

    this.emitEvent('stateChanged');
    return { success: true };
  }

  // Helper methods for game logic
  private generateItemId(): string {
    const currentTick = this.getCurrentTick();
    return `item_${currentTick}_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * Get tile at position using comprehensive lookup: building layer > game map > world generation
   * This is the single source of truth for tile lookup across the application
   */
  public getTile(
    x: number,
    y: number,
    gameMap?: Record<string, string>,
    buildingLayer?: Record<string, string>
  ): string | undefined {
    const key = `${x},${y}`;
    const actualGameMap = gameMap || this.state.gameMap;
    const actualBuildingLayer = buildingLayer || this.state.buildingLayer;

    // Check building layer first (buildings are rendered on top)
    if (actualBuildingLayer && key in actualBuildingLayer) {
      return actualBuildingLayer[key];
    }

    // Check terrain modifications next (for collected resources)
    if (key in actualGameMap) {
      return actualGameMap[key];
    }

    // Generate world tile (entities should be generated during world initialization)
    const terrain = generateTerrainTile(x, y); // Use new terrain generator that excludes resources

    return terrain;
  }

  private getMinerOutputType(
    tile: string | undefined,
    building: Building | null
  ): ItemType | null {
    if (building?.id === BuildingType.SAND_DREDGER) {
      return 'sand';
    }
    switch (tile) {
      case GAME_CONFIG.IRON_NODE_CHARACTER:
        return 'iron-ore';
      case GAME_CONFIG.COPPER_NODE_CHARACTER:
        return 'copper-ore';
      default:
        return null;
    }
  }

  private getAdjacentPositions(
    pos: Position
  ): { position: Position; direction: PlayerFacing }[] {
    return [
      { position: { x: pos.x, y: pos.y - 1 }, direction: 'up' as PlayerFacing },
      {
        position: { x: pos.x, y: pos.y + 1 },
        direction: 'down' as PlayerFacing,
      },
      {
        position: { x: pos.x - 1, y: pos.y },
        direction: 'left' as PlayerFacing,
      },
      {
        position: { x: pos.x + 1, y: pos.y },
        direction: 'right' as PlayerFacing,
      },
    ];
  }

  private isSplitter(position: Position): boolean {
    const { building } = getBuildingAtPosition(
      position.x,
      position.y,
      this.state.buildingLayer
    );
    return building?.id === BuildingType.SPLITTER;
  }

  private isUndergroundBelt(position: Position): boolean {
    const { building } = getBuildingAtPosition(
      position.x,
      position.y,
      this.state.buildingLayer
    );

    return building?.id === BuildingType.UNDERGROUND_BELT;
  }

  private findUndergroundConnection(
    entryPosition: Position,
    direction: PlayerFacing
  ): { exitBuilding: Position; exitOutput: Position } | null {
    let current = { ...entryPosition };

    for (
      let distance = 1;
      distance <= UNDERGROUND_BELT_MAX_DISTANCE;
      distance += 1
    ) {
      current = this.getNextPosition(current, direction);
      const { building } = getBuildingAtPosition(
        current.x,
        current.y,
        this.state.buildingLayer
      );

      if (building?.id !== BuildingType.UNDERGROUND_BELT) {
        continue;
      }

      const outputPosition = this.getNextPosition(current, direction);
      if (!this.hasBelt(outputPosition)) {
        continue;
      }

      const beltDirection = getBeltDirectionFromCharacter(
        this.state.buildingLayer,
        outputPosition
      );

      if (beltDirection !== direction) {
        continue;
      }

      return {
        exitBuilding: { ...current },
        exitOutput: outputPosition,
      };
    }

    return null;
  }

  private hasBelt(position: Position): boolean {
    const { building } = getBuildingAtPosition(
      position.x,
      position.y,
      this.state.buildingLayer
    );
    if (!building) return false;

    // Get the building character at this position to check belt direction
    const key = `${position.x},${position.y}`;
    const buildingChar = this.state.buildingLayer[key];
    if (!buildingChar) return false;

    return (
      getBeltDirectionFromCharacter(this.state.buildingLayer, position) !== null
    );
  }

  private hasAssembler(position: Position): boolean {
    const { building } = getBuildingAtPosition(
      position.x,
      position.y,
      this.state.buildingLayer
    );
    return (
      building?.id === BuildingType.ASSEMBLER ||
      building?.id === BuildingType.FACTORY
    );
  }

  private hasResearchFacility(position: Position): boolean {
    const { building } = getBuildingAtPosition(
      position.x,
      position.y,
      this.state.buildingLayer
    );

    return building?.id === BuildingType.RESEARCH_FACILITY;
  }

  private hasChest(position: Position): boolean {
    const { building } = getBuildingAtPosition(
      position.x,
      position.y,
      this.state.buildingLayer
    );

    return (building?.stackCapacity ?? 0) > 0;
  }

  private tryAcceptItemIntoResearchFacility(
    position: Position,
    item: BeltItem,
    researchState: ResearchState
  ): {
    state: ResearchState;
    accepted: boolean;
    acceptedAmount: number;
    completed: boolean;
    completedResearchId: ResearchId | null;
  } {
    const { centerPosition } = getBuildingAtPosition(
      position.x,
      position.y,
      this.state.buildingLayer
    );
    const facilityKey = centerPosition
      ? `${centerPosition.x},${centerPosition.y}`
      : `${position.x},${position.y}`;

    const selectedResearch = getActiveResearchId(researchState);
    if (!selectedResearch) {
      beltLogger.debug('Research facility has no active research', {
        facilityKey,
        itemType: item.item,
      });
      return {
        state: researchState,
        accepted: false,
        acceptedAmount: 0,
        completed: false,
        completedResearchId: null,
      };
    }

    const amountToAdd = item.count ?? 1;
    const previousProgress =
      researchState.progress[selectedResearch]?.[item.item] ?? 0;
    const result = addResearchProgress(
      researchState,
      selectedResearch,
      item.item,
      amountToAdd
    );

    const updatedProgress =
      result.state.progress[selectedResearch]?.[item.item] ?? previousProgress;
    const acceptedAmount = Math.max(0, updatedProgress - previousProgress);

    if (acceptedAmount > 0) {
      this.handleResearchDataProduction(acceptedAmount);

      if (result.completed) {
        beltLogger.info(
          'Research facility completed requirements for research',
          {
            facilityKey,
            researchId: selectedResearch,
            itemType: item.item,
          }
        );
      } else {
        beltLogger.debug('Research facility accepted item', {
          facilityKey,
          researchId: selectedResearch,
          itemType: item.item,
          amount: acceptedAmount,
        });
      }

      return {
        state: result.state,
        accepted: true,
        acceptedAmount,
        completed: result.completed,
        completedResearchId: result.completed ? selectedResearch : null,
      };
    }

    beltLogger.debug('Research facility rejected item', {
      facilityKey,
      researchId: selectedResearch,
      itemType: item.item,
    });

    return {
      state: researchState,
      accepted: false,
      acceptedAmount: 0,
      completed: false,
      completedResearchId: null,
    };
  }

  private findBuildingByCharacter(character: string): Building | null {
    // First check if it's a standard building character
    const standardBuilding = Object.values(BUILDINGS).find(
      b => b.character === character
    );
    if (standardBuilding) {
      return standardBuilding;
    }

    // Check if it's a directional belt character
    const beltDirections = Object.values(BELT_CHARACTERS) as string[];
    if (beltDirections.includes(character)) {
      return BUILDINGS.BELT;
    }

    const fastBeltDirections = Object.values(FAST_BELT_CHARACTERS) as string[];
    if (fastBeltDirections.includes(character)) {
      return BUILDINGS.FAST_BELT;
    }

    return null;
  }

  private getDirectionDelta(direction: PlayerFacing): { x: number; y: number } {
    switch (direction) {
      case 'up':
        return { x: 0, y: -1 };
      case 'down':
        return { x: 0, y: 1 };
      case 'left':
        return { x: -1, y: 0 };
      case 'right':
        return { x: 1, y: 0 };
      default:
        return { x: 0, y: 0 };
    }
  }

  private getNextPosition(pos: Position, direction: PlayerFacing): Position {
    switch (direction) {
      case 'up':
        return { x: pos.x, y: pos.y - 1 };
      case 'down':
        return { x: pos.x, y: pos.y + 1 };
      case 'left':
        return { x: pos.x - 1, y: pos.y };
      case 'right':
        return { x: pos.x + 1, y: pos.y };
      default:
        return pos;
    }
  }

  private getOppositeDirection(direction: PlayerFacing): PlayerFacing {
    switch (direction) {
      case 'up':
        return 'down';
      case 'down':
        return 'up';
      case 'left':
        return 'right';
      case 'right':
        return 'left';
      default:
        return direction;
    }
  }

  private normalizeRejectedItemTimestamp(
    timestamp: number,
    currentTick: number,
    nowMs: number
  ): number {
    if (!Number.isFinite(timestamp)) {
      return currentTick;
    }

    if (timestamp > LEGACY_MS_TIMESTAMP_THRESHOLD) {
      const elapsedMs = Math.max(0, nowMs - timestamp);
      const elapsedTicks = Math.round(elapsedMs / TICK_DURATION_MS);
      return Math.max(0, currentTick - elapsedTicks);
    }

    return Math.max(0, Math.round(timestamp));
  }

  private normalizeRejectedItemsLayer(
    layer: Record<string, { timestamp: number; itemType: ItemType }>
  ): Record<string, { timestamp: number; itemType: ItemType }> {
    const currentTick = this.getCurrentTick();
    const nowMs = Date.now();

    return Object.fromEntries(
      Object.entries(layer)
        .filter(
          (
            entry
          ): entry is [string, { timestamp: number; itemType: ItemType }] =>
            entry[1] !== undefined && entry[1] !== null
        )
        .map(([key, rejection]) => [
          key,
          {
            itemType: rejection.itemType,
            timestamp: this.normalizeRejectedItemTimestamp(
              rejection.timestamp,
              currentTick,
              nowMs
            ),
          },
        ])
    );
  }

  private pruneProductionHistory(currentTick: number): void {
    if (this.state.itemProductionHistory.length === 0) {
      return;
    }

    const cutoffTick = Math.max(
      0,
      currentTick - PRODUCTION_HISTORY_MAX_WINDOW_TICKS
    );
    const filteredHistory = this.state.itemProductionHistory.filter(
      entry => entry.tick >= cutoffTick
    );

    if (filteredHistory.length !== this.state.itemProductionHistory.length) {
      this.state.itemProductionHistory = filteredHistory;
    }
  }

  private recordItemProduction(itemType: ItemType, amount: number): void {
    if (amount <= 0) {
      return;
    }

    const currentTick = this.getCurrentTick();
    const history = [...this.state.itemProductionHistory];
    const lastEntry = history[history.length - 1];

    if (lastEntry && lastEntry.tick === currentTick) {
      history[history.length - 1] = {
        tick: currentTick,
        amounts: {
          ...lastEntry.amounts,
          [itemType]: (lastEntry.amounts[itemType] ?? 0) + amount,
        },
      };
    } else {
      history.push({
        tick: currentTick,
        amounts: { [itemType]: amount },
      });
    }

    this.state.itemProductionHistory = history;
    this.pruneProductionHistory(currentTick);

    this.handleItemProductionForAchievements(itemType, amount);
  }

  private handleItemProductionForAchievements(
    itemType: ItemType,
    amount: number
  ): void {
    if (amount <= 0) {
      return;
    }

    const achievementContext: AchievementContext = {};
    let shouldTrigger = false;
    let progressUpdated = false;

    if (itemType === 'iron-ore' || itemType === 'copper-ore') {
      const previousTotal = this.totalOreExtracted;
      this.totalOreExtracted += amount;
      progressUpdated = true;

      if (
        !this.isAchievementUnlocked(AchievementType.RESOURCE_BARON) &&
        previousTotal < GameEngine.ORE_EXTRACTION_TARGET &&
        this.totalOreExtracted >= GameEngine.ORE_EXTRACTION_TARGET
      ) {
        achievementContext.totalOreExtracted = this.totalOreExtracted;
        shouldTrigger = true;
      }
    }

    if (itemType === 'iron-plate') {
      const previousTotal = this.totalIronPlatesProduced;
      this.totalIronPlatesProduced += amount;
      progressUpdated = true;

      if (
        !this.isAchievementUnlocked(AchievementType.IRON_REVOLUTION) &&
        previousTotal < GameEngine.IRON_PLATE_TARGET &&
        this.totalIronPlatesProduced >= GameEngine.IRON_PLATE_TARGET
      ) {
        achievementContext.totalIronPlatesProduced =
          this.totalIronPlatesProduced;
        shouldTrigger = true;
      }
    }

    if (shouldTrigger) {
      this.triggerAchievementCheck(achievementContext, { suppressEvent: true });
    }

    if (progressUpdated) {
      this.refreshAchievementProgress();
    }
  }

  private handleResearchDataProduction(amount: number): void {
    if (amount <= 0) {
      return;
    }

    const previousTotal = this.totalResearchDataProduced;
    this.totalResearchDataProduced += amount;
    this.refreshAchievementProgress();

    if (
      this.isAchievementUnlocked(AchievementType.DATA_DRIP) ||
      previousTotal >= GameEngine.RESEARCH_DATA_TARGET ||
      this.totalResearchDataProduced < GameEngine.RESEARCH_DATA_TARGET
    ) {
      return;
    }

    this.triggerAchievementCheck(
      { totalResearchDataProduced: this.totalResearchDataProduced },
      { suppressEvent: true }
    );
  }

  // Mining logic
  private processMiningOutputs(): void {
    const currentTick = this.getCurrentTick();
    const beltCapacityMultiplier = getBeltCapacityMultiplier(
      this.state.researchState
    );

    // Find all miners in the building layer
    const allBuildings = Object.entries(this.state.buildingLayer);
    let newBeltContent = { ...this.state.beltContentLayer };
    const newMinerTimers = { ...this.state.minerTimerLayer };
    const newMinerOutputBuffers = { ...this.state.minerOutputBufferLayer };

    for (const [posKey, buildingChar] of allBuildings) {
      // Check if this is a miner
      const building = this.findBuildingByCharacter(buildingChar);
      if (building && building.category === 'mining') {
        const [xStr, yStr] = posKey.split(',');
        if (!xStr || !yStr) continue;
        const minerPos = { x: parseInt(xStr, 10), y: parseInt(yStr, 10) };
        const minerOutputIntervalTicks =
          this.getMinerOutputIntervalTicks(building);
        const bufferCapacity = building.miningBufferCapacity ?? 0;
        const hasBuffer = bufferCapacity > 0;
        const outputPerCycle = Math.max(1, building.miningOutputPerCycle ?? 1);

        // Check if enough ticks have passed since last output
        const lastOutputTick = newMinerTimers[posKey];

        let minerStartTick: number;
        if (lastOutputTick === undefined) {
          // New miner - initialize with current tick
          minerStartTick = currentTick;
          newMinerTimers[posKey] = minerStartTick;
          continue; // Skip this cycle, will output after interval
        } else if (lastOutputTick > currentTick) {
          // Invalid timer - restart
          minerStartTick = currentTick;
          newMinerTimers[posKey] = minerStartTick;
          continue;
        } else {
          minerStartTick = lastOutputTick;
        }

        const key = `${minerPos.x},${minerPos.y}`;
        const resourceTile =
          this.state.gameMap[key] ||
          generateTerrainTile(minerPos.x, minerPos.y);
        const outputType = this.getMinerOutputType(resourceTile, building);
        const nextOutputTick = minerStartTick + minerOutputIntervalTicks;

        if (hasBuffer) {
          let bufferedCount = newMinerOutputBuffers[posKey] ?? 0;

          if (outputType && currentTick >= nextOutputTick) {
            const availableCapacity = Math.max(
              0,
              bufferCapacity - bufferedCount
            );
            const produceCount = Math.min(outputPerCycle, availableCapacity);

            if (produceCount > 0) {
              bufferedCount += produceCount;
              newMinerOutputBuffers[posKey] = bufferedCount;
              newMinerTimers[posKey] =
                minerStartTick + minerOutputIntervalTicks;
            }
          }

          if (outputType && bufferedCount > 0) {
            const adjacentPositions = this.getAdjacentPositions(minerPos);
            let itemsRemaining = bufferedCount;

            while (itemsRemaining > 0) {
              let placed = false;

              for (const { position } of adjacentPositions) {
                if (!this.hasBelt(position)) {
                  continue;
                }

                const beltKey = `${position.x},${position.y}`;
                const nextContent = addItemToBelt(
                  position,
                  outputType,
                  newBeltContent,
                  beltCapacityMultiplier,
                  () => this.generateItemId()
                );

                if (nextContent === newBeltContent) {
                  continue;
                }

                newBeltContent = nextContent;
                itemsRemaining -= 1;
                this.handleItemProductionForAchievements(outputType, 1);

                const updatedStack = newBeltContent[beltKey]?.[0];
                const stackCount =
                  updatedStack?.count ?? (updatedStack ? 1 : 0);

                beltLogger.info('Miner outputted buffered item to belt', {
                  minerPos: posKey,
                  beltPos: beltKey,
                  itemType: outputType,
                  bufferRemaining: itemsRemaining,
                  stackCount,
                  maxCapacity: beltCapacityMultiplier,
                });

                placed = true;
                break;
              }

              if (!placed) {
                break;
              }
            }

            if (itemsRemaining > 0) {
              newMinerOutputBuffers[posKey] = itemsRemaining;
            } else {
              delete newMinerOutputBuffers[posKey];
            }
          } else if (bufferedCount > 0) {
            newMinerOutputBuffers[posKey] = bufferedCount;
          } else {
            delete newMinerOutputBuffers[posKey];
          }

          continue;
        }

        if (!outputType) {
          continue;
        }

        if (currentTick < nextOutputTick) {
          continue; // Not ready yet
        }

        const adjacentPositions = this.getAdjacentPositions(minerPos);
        let itemsToOutput = outputPerCycle;

        while (itemsToOutput > 0) {
          let placed = false;

          for (const { position } of adjacentPositions) {
            if (!this.hasBelt(position)) {
              continue;
            }

            const beltKey = `${position.x},${position.y}`;
            const nextContent = addItemToBelt(
              position,
              outputType,
              newBeltContent,
              beltCapacityMultiplier,
              () => this.generateItemId()
            );

            if (nextContent === newBeltContent) {
              continue;
            }

            newBeltContent = nextContent;
            this.recordItemProduction(outputType, 1);
            itemsToOutput -= 1;

            const updatedStack = newBeltContent[beltKey]?.[0];
            const stackCount = updatedStack?.count ?? (updatedStack ? 1 : 0);

            beltLogger.info('Miner outputted item to belt', {
              minerPos: posKey,
              beltPos: beltKey,
              itemType: outputType,
              stackCount,
              maxCapacity: beltCapacityMultiplier,
            });

            placed = true;
            break;
          }

          if (!placed) {
            break;
          }
        }

        if (itemsToOutput < outputPerCycle) {
          newMinerTimers[posKey] = minerStartTick + minerOutputIntervalTicks;
        }
      }
    }

    this.state.beltContentLayer = newBeltContent;
    this.state.minerTimerLayer = newMinerTimers;
    this.state.minerOutputBufferLayer = newMinerOutputBuffers;
  }

  // Belt movement logic
  private processBeltMovement(): void {
    const newBeltContent: Record<string, BeltItem[]> = {};
    let newAssemblerInventory = { ...this.state.assemblerInventoryLayer };
    let newChestInventory = { ...this.state.chestInventoryLayer };
    let beltContentWithChestOutputs: Record<string, BeltItem[]> = {
      ...this.state.beltContentLayer,
    };
    let newResearchState = this.state.researchState;
    const newRejectedItems = { ...this.state.rejectedItemsLayer };
    const rejectionTimestamp = this.getCurrentTick();
    const teleportedItems: Record<string, BeltItem[]> = {};
    const beltSpeedMultiplier = Math.max(
      0.1,
      getBeltSpeedMultiplier(this.state.researchState)
    );
    const beltCapacityMultiplier = getBeltCapacityMultiplier(
      this.state.researchState
    );
    const powerMultiplier = Math.max(this.state.powerSatisfaction, 0);
    const itemSpeed = Math.max(0, beltSpeedMultiplier * powerMultiplier);
    const deltaTime = 1 / 60; // 60 FPS

    Object.entries(this.state.chestInventoryLayer).forEach(
      ([chestKey, inventory]) => {
        if (!inventory) {
          return;
        }

        const [xStr, yStr] = chestKey.split(',');
        if (!xStr || !yStr) {
          return;
        }

        const chestPos = { x: parseInt(xStr, 10), y: parseInt(yStr, 10) };
        if (Number.isNaN(chestPos.x) || Number.isNaN(chestPos.y)) {
          return;
        }

        const { building } = getBuildingAtPosition(
          chestPos.x,
          chestPos.y,
          this.state.buildingLayer
        );

        if (!building || !(building.stackCapacity ?? 0)) {
          return;
        }

        const adjacentPositions = this.getAdjacentPositions(chestPos);
        const updatedChestItems = { ...inventory.items };
        let chestChanged = false;

        adjacentPositions.forEach(({ position }) => {
          if (!this.hasBelt(position)) {
            return;
          }

          const beltDirection = getBeltDirectionFromCharacter(
            this.state.buildingLayer,
            position
          );

          if (!beltDirection) {
            return;
          }

          const sourcePos = this.getNextPosition(
            position,
            this.getOppositeDirection(beltDirection)
          );

          if (sourcePos.x !== chestPos.x || sourcePos.y !== chestPos.y) {
            return;
          }

          const beltKey = `${position.x},${position.y}`;
          const itemTypeToOutput = ITEM_TYPES.find(
            itemType => (updatedChestItems[itemType] ?? 0) > 0
          );

          if (!itemTypeToOutput) {
            return;
          }

          const currentCount = updatedChestItems[itemTypeToOutput] ?? 0;
          if (currentCount <= 0) {
            return;
          }

          const existingItems = beltContentWithChestOutputs[beltKey] || [];
          const existingStack = existingItems[0];
          const existingCount = existingStack?.count ?? (existingStack ? 1 : 0);
          const canCreateStack = !existingStack;
          const canStackExisting =
            existingStack?.item === itemTypeToOutput &&
            existingCount < beltCapacityMultiplier;

          if (!canCreateStack && !canStackExisting) {
            return;
          }

          const nextContent = addItemToBelt(
            position,
            itemTypeToOutput,
            beltContentWithChestOutputs,
            beltCapacityMultiplier,
            () => this.generateItemId()
          );

          if (nextContent === beltContentWithChestOutputs) {
            return;
          }

          beltContentWithChestOutputs = nextContent;
          const updatedStack = beltContentWithChestOutputs[beltKey]?.[0];
          const updatedCount = updatedStack?.count ?? (updatedStack ? 1 : 0);
          updatedChestItems[itemTypeToOutput] = currentCount - 1;
          chestChanged = true;

          beltLogger.info('Chest outputted item to belt', {
            chestPos: chestKey,
            beltPos: beltKey,
            itemType: itemTypeToOutput,
            stackCount: updatedCount,
            maxCapacity: beltCapacityMultiplier,
          });
        });

        if (chestChanged) {
          const normalizedItems = { ...updatedChestItems };
          ITEM_TYPES.forEach(itemType => {
            const value = normalizedItems[itemType] ?? 0;
            normalizedItems[itemType] = value > 0 ? value : 0;
          });

          newChestInventory = {
            ...newChestInventory,
            [chestKey]: { items: normalizedItems },
          };
        }
      }
    );

    Object.entries(beltContentWithChestOutputs).forEach(([posKey, items]) => {
      const [xStr, yStr] = posKey.split(',');
      if (!xStr || !yStr) return;
      const pos = { x: parseInt(xStr, 10), y: parseInt(yStr, 10) };

      const beltChar = this.state.buildingLayer[posKey];
      const beltDirection = beltChar
        ? getBeltDirectionFromCharacter(this.state.buildingLayer, pos)
        : null;

      if (!beltDirection || items.length === 0) {
        newBeltContent[posKey] = items;
        return;
      }

      const updatedItems: BeltItem[] = [];

      items.forEach(originalItem => {
        const stackCount = originalItem.count ?? 1;
        const item: BeltItem =
          originalItem.count === stackCount
            ? originalItem
            : { ...originalItem, count: stackCount };
        const potentialNewProgress = Math.min(
          1,
          item.progress + itemSpeed * deltaTime
        );

        if (potentialNewProgress >= 1) {
          const nextPos = this.getNextPosition(pos, beltDirection);
          const nextPosKey = `${nextPos.x},${nextPos.y}`;
          let remainingCount = stackCount;

          if (this.hasResearchFacility(nextPos)) {
            const facilityResult = this.tryAcceptItemIntoResearchFacility(
              nextPos,
              item,
              newResearchState
            );

            newResearchState = facilityResult.state;

            if (facilityResult.completedResearchId) {
              this.state = {
                ...this.state,
                researchState: newResearchState,
              };
              this.unlockResearch(facilityResult.completedResearchId, {
                suppressEvent: true,
              });
              newResearchState = this.state.researchState;
            }

            const acceptedAmount = facilityResult.acceptedAmount ?? 0;
            if (acceptedAmount > 0) {
              remainingCount = Math.max(0, remainingCount - acceptedAmount);
            }

            if (remainingCount <= 0) {
              return;
            }

            if (acceptedAmount === 0) {
              newRejectedItems[nextPosKey] = {
                timestamp: rejectionTimestamp,
                itemType: item.item,
              };
            }

            updatedItems.push({
              ...item,
              progress: 0.99,
              count: remainingCount,
            });
            return;
          }

          if (this.hasAssembler(nextPos)) {
            const assemblerInventory = getAssemblerInventory(
              newAssemblerInventory,
              this.state.buildingLayer,
              nextPos
            );

            if (assemblerInventory && assemblerInventory.recipe) {
              const isNeeded = assemblerInventory.recipe.inputs.some(
                (input: RecipeIngredient) => input.item === item.item
              );

              if (isNeeded) {
                const recipeInput = assemblerInventory.recipe.inputs.find(
                  (input: RecipeIngredient) => input.item === item.item
                );
                const maxInputOutputsMultiplier = getMaxInputOutputsMultiplier(
                  nextPos,
                  this.state.buildingLayer,
                  this.state.researchState
                );
                const maxCapacity =
                  (recipeInput?.count || 0) * maxInputOutputsMultiplier;
                const currentCount = assemblerInventory.items[item.item] || 0;
                const spaceAvailable = Math.max(0, maxCapacity - currentCount);

                if (recipeInput && spaceAvailable > 0) {
                  const accepted = Math.min(remainingCount, spaceAvailable);
                  if (accepted > 0) {
                    const updatedInventory = {
                      ...assemblerInventory,
                      items: {
                        ...assemblerInventory.items,
                        [item.item]: currentCount + accepted,
                      },
                    };
                    newAssemblerInventory = setAssemblerInventory(
                      newAssemblerInventory,
                      this.state.buildingLayer,
                      nextPos,
                      updatedInventory
                    );
                    remainingCount -= accepted;

                    if (remainingCount <= 0) {
                      return;
                    }

                    updatedItems.push({
                      ...item,
                      progress: 0.99,
                      count: remainingCount,
                    });
                    return;
                  }
                }
              }

              newRejectedItems[nextPosKey] = {
                timestamp: rejectionTimestamp,
                itemType: item.item,
              };
              updatedItems.push({ ...item, progress: 0.99, count: stackCount });
              return;
            }
          }

          if (this.hasChest(nextPos)) {
            const chestResult = addItemsToChest(
              newChestInventory,
              this.state.buildingLayer,
              nextPos,
              item.item,
              remainingCount
            );

            if (chestResult.added > 0) {
              newChestInventory = chestResult.layer;
              remainingCount -= chestResult.added;

              beltLogger.info('Item stored in chest', {
                chestPos: nextPosKey,
                itemType: item.item,
              });

              if (remainingCount <= 0) {
                return;
              }

              updatedItems.push({
                ...item,
                progress: 0.99,
                count: remainingCount,
              });
              return;
            }

            newRejectedItems[nextPosKey] = {
              timestamp: rejectionTimestamp,
              itemType: item.item,
            };
            updatedItems.push({ ...item, progress: 0.99, count: stackCount });
            return;
          }

          if (this.isSplitter(nextPos)) {
            if (remainingCount !== stackCount) {
              updatedItems.push({
                ...item,
                progress: 0.99,
                count: remainingCount,
              });
              return;
            }

            const splitterOutputs = this.getAdjacentPositions(nextPos);
            let distributed = false;

            splitterOutputs.forEach(({ position: candidatePos }) => {
              if (!this.hasBelt(candidatePos)) {
                return;
              }

              const candidateDirection = getBeltDirectionFromCharacter(
                this.state.buildingLayer,
                candidatePos
              );

              if (!candidateDirection) {
                return;
              }

              const delta = this.getDirectionDelta(candidateDirection);
              if (
                candidatePos.x + delta.x === nextPos.x &&
                candidatePos.y + delta.y === nextPos.y
              ) {
                return;
              }

              const candidateKey = `${candidatePos.x},${candidatePos.y}`;
              const existingItems =
                newBeltContent[candidateKey] ??
                this.state.beltContentLayer[candidateKey] ??
                [];

              if (existingItems.length > 0) {
                return;
              }

              newBeltContent[candidateKey] = [
                {
                  ...item,
                  progress: 0,
                  id: this.generateItemId(),
                  count: stackCount,
                },
              ];
              distributed = true;

              beltLogger.debug('Splitter routed item', {
                splitterPos: nextPosKey,
                outputPos: candidateKey,
                itemType: item.item,
                stackCount,
              });
            });

            if (distributed) {
              return;
            }

            updatedItems.push({ ...item, progress: 0.99, count: stackCount });
            return;
          }

          if (this.isUndergroundBelt(nextPos)) {
            if (remainingCount !== stackCount) {
              updatedItems.push({
                ...item,
                progress: 0.99,
                count: remainingCount,
              });
              return;
            }

            const connection = this.findUndergroundConnection(
              nextPos,
              beltDirection
            );

            if (connection) {
              const exitKey = `${connection.exitOutput.x},${connection.exitOutput.y}`;
              const queuedItems = teleportedItems[exitKey] ?? [];
              const existingItems =
                newBeltContent[exitKey] ??
                this.state.beltContentLayer[exitKey] ??
                [];

              if (existingItems.length === 0 && queuedItems.length === 0) {
                teleportedItems[exitKey] = [
                  ...queuedItems,
                  {
                    ...item,
                    progress: 0,
                    id: this.generateItemId(),
                    count: stackCount,
                  },
                ];

                beltLogger.debug('Teleported item through underground belt', {
                  entry: nextPosKey,
                  exit: exitKey,
                  itemType: item.item,
                  stackCount,
                });
                return;
              }
            }

            updatedItems.push({ ...item, progress: 0.99, count: stackCount });
            return;
          }

          if (this.hasBelt(nextPos)) {
            if (remainingCount !== stackCount) {
              updatedItems.push({
                ...item,
                progress: 0.99,
                count: remainingCount,
              });
              return;
            }

            const destinationOccupied =
              (beltContentWithChestOutputs[nextPosKey]?.length ?? 0) > 0 ||
              (newBeltContent[nextPosKey]?.length ?? 0) > 0;

            if (!destinationOccupied) {
              newBeltContent[nextPosKey] = [
                {
                  ...item,
                  progress: 0,
                  id: this.generateItemId(),
                  count: stackCount,
                },
              ];
              return;
            }

            updatedItems.push({ ...item, progress: 0.99, count: stackCount });
            return;
          }

          updatedItems.push({
            ...item,
            progress: 0.99,
            count: remainingCount,
          });
          return;
        }

        updatedItems.push({
          ...item,
          progress: potentialNewProgress,
          count: stackCount,
        });
      });

      newBeltContent[posKey] = updatedItems;
    });

    Object.entries(teleportedItems).forEach(([posKey, items]) => {
      const existingItems = newBeltContent[posKey] ?? [];
      newBeltContent[posKey] = [...existingItems, ...items];
    });

    this.state.beltContentLayer = newBeltContent;
    this.state.assemblerInventoryLayer = newAssemblerInventory;
    this.state.chestInventoryLayer = newChestInventory;
    this.state.researchState = newResearchState;
    this.state.rejectedItemsLayer = newRejectedItems;
  }

  // Assembler crafting logic
  private processAssemblerCrafting(): void {
    const result = processAssemblerCrafting(
      this.state.assemblerInventoryLayer,
      this.state.buildingLayer,
      this.state.beltContentLayer,
      this.state.researchState,
      this.state.powerSatisfaction,
      {
        tickSystem: this.tickSystem,
        generateItemId: () => this.generateItemId(),
        onOutputItem: (itemType: ItemType, amount: number) => {
          this.recordItemProduction(itemType, amount);
        },
      }
    );

    this.state = {
      ...this.state,
      assemblerInventoryLayer: result.updatedAssemblerInventory,
      beltContentLayer: result.updatedBeltContent,
    };
  }

  private processHandCrafting(): void {
    if (this.state.handCraftingQueue.length === 0) {
      return;
    }

    const currentQueue = [...this.state.handCraftingQueue];
    const activeEntry = currentQueue[0];
    if (!activeEntry) {
      return;
    }

    const recipe = RECIPES_BY_ID[activeEntry.recipeId as Recipe['id']];

    if (!recipe) {
      currentQueue.shift();
      this.state = {
        ...this.state,
        handCraftingQueue: currentQueue,
      };
      return;
    }

    const ticksPerCraft = getHandCraftingTicksPerCraft(recipe);
    let updatedEntry: HandCraftingQueueEntry = {
      ...activeEntry,
      ticksPerCraft,
      progressTicks: activeEntry.progressTicks + 1,
    };

    let playerInventory = this.state.playerInventory;
    let inventoryChanged = false;

    if (updatedEntry.progressTicks >= ticksPerCraft) {
      updatedEntry = {
        ...updatedEntry,
        progressTicks: updatedEntry.progressTicks - ticksPerCraft,
        remainingCount: updatedEntry.remainingCount - 1,
      };

      for (const output of recipe.outputs) {
        if (output.count <= 0) {
          continue;
        }
        const { inventory, added } = addItemToPlayerInventory(
          playerInventory,
          output.item,
          output.count
        );
        if (added > 0) {
          playerInventory = inventory;
          inventoryChanged = true;
          this.recordItemProduction(output.item, added);
        }
      }
    }

    const updatedQueue: HandCraftingQueueEntry[] =
      updatedEntry.remainingCount > 0
        ? [{ ...updatedEntry }, ...currentQueue.slice(1)]
        : [...currentQueue.slice(1)];

    this.state = {
      ...this.state,
      playerInventory: inventoryChanged
        ? playerInventory
        : this.state.playerInventory,
      handCraftingQueue: updatedQueue,
    };
  }

  // Main tick processing - this is where all game logic runs synchronously
  processTick(): void {
    if (this.isTickProcessing) {
      buildingLogger.warn('Tick already in progress, skipping');
      return;
    }

    this.isTickProcessing = true;

    try {
      const currentTick = this.getCurrentTick();
      this.state.currentTick = currentTick;
      this.pruneProductionHistory(currentTick);

      // 1. Update input manager and process input commands
      this.inputManager.tick();
      this.processInputCommands();

      this.recalculatePowerNetwork(true);

      // 2. Process movement queue
      this.processMovementQueue();

      // 3. Process miner outputs
      this.processMiningOutputs();

      // 4. Process belt item movement
      this.processBeltMovement();

      // 5. Process assembler crafting
      this.processAssemblerCrafting();

      // 6. Process hand crafting queue
      this.processHandCrafting();

      // 7. Update all entities
      this.entityManager.updateAll(16.67); // ~16.67ms per tick at 60 FPS

      // 8. Clean up old rejection indicators
      const cleanupTick = this.state.currentTick;
      const nowMs = Date.now();
      const cleanedRejections = { ...this.state.rejectedItemsLayer };
      let rejectionsCleaned = false;

      Object.entries(cleanedRejections).forEach(([posKey, rejection]) => {
        if (!rejection) {
          delete cleanedRejections[posKey];
          rejectionsCleaned = true;
          return;
        }

        const normalizedTimestamp = this.normalizeRejectedItemTimestamp(
          rejection.timestamp,
          cleanupTick,
          nowMs
        );

        if (
          cleanupTick - normalizedTimestamp >
          REJECTION_DISPLAY_DURATION_TICKS
        ) {
          delete cleanedRejections[posKey];
          rejectionsCleaned = true;
          return;
        }

        if (normalizedTimestamp !== rejection.timestamp) {
          cleanedRejections[posKey] = {
            ...rejection,
            timestamp: normalizedTimestamp,
          };
          rejectionsCleaned = true;
        }
      });

      if (rejectionsCleaned) {
        this.state.rejectedItemsLayer = cleanedRejections;
      }

      // Always emit state changed after tick processing
      this.soundEngine.update(this.state);
      this.emitEvent('stateChanged');
    } catch (error) {
      buildingLogger.error('Error processing game tick', { error });
    } finally {
      this.isTickProcessing = false;
    }
  }

  // Process all queued input commands
  private processInputCommands(): void {
    const commands = this.inputManager.getGameCommands();

    for (const command of commands) {
      try {
        this.processInputCommand(command);
      } catch (error) {
        buildingLogger.error('Error processing input command', {
          command,
          error,
        });
      }
    }
  }

  // Process a single input command
  private processInputCommand(command: InputCommand): void {
    switch (command.type) {
      case InputCommandType.MOVE:
        this.handleMoveCommand(command);
        break;

      case InputCommandType.BUILD_DECONSTRUCT:
        this.handleBuildDeconstructCommand(command);
        break;

      case InputCommandType.COLLECT_RESOURCE:
        this.handleCollectResourceCommand(command);
        break;

      case InputCommandType.ZOOM_IN:
        this.zoomIn();
        break;

      case InputCommandType.ZOOM_OUT:
        this.zoomOut();
        break;

      case InputCommandType.ZOOM_RESET:
        this.resetZoom();
        break;

      case InputCommandType.CLEAR_BELT_ITEMS:
        this.clearBeltItems();
        break;

      case InputCommandType.SAVE_GAME:
        this.handleSaveGameCommand();
        break;

      // Build menu commands now handled by GameEngine
      case InputCommandType.TOGGLE_BUILD_MENU:
        this.handleToggleBuildMenuCommand();
        break;

      case InputCommandType.SELECT_BUILD_CATEGORY:
        this.handleSelectBuildCategoryCommand(command);
        break;

      case InputCommandType.SELECT_BUILDING:
        this.handleSelectBuildingCommand(command);
        break;

      // UI menu toggle commands now handled by GameEngine
      case InputCommandType.TOGGLE_SAVE_MENU:
        this.handleToggleSaveMenuCommand();
        break;

      case InputCommandType.TOGGLE_DEBUG_MENU:
        this.handleToggleDebugMenuCommand();
        break;

      case InputCommandType.TOGGLE_HELP_MENU:
        this.handleToggleHelpMenuCommand();
        break;

      case InputCommandType.TOGGLE_RESEARCH_MENU:
        this.handleToggleResearchMenuCommand();
        break;

      case InputCommandType.TOGGLE_PRODUCTION_MENU:
        this.handleToggleProductionMenuCommand();
        break;

      case InputCommandType.TOGGLE_LEFT_PANEL:
        this.handleToggleLeftPanelCommand();
        break;

      case InputCommandType.TOGGLE_ACHIEVEMENT_MENU:
        this.handleToggleAchievementMenuCommand();
        break;

      case InputCommandType.CLOSE_ALL_MENUS:
        this.handleCloseTopMenuCommand();
        break;

      case InputCommandType.TAKE_ACTION:
        this.handleTakeActionCommand(command);
        break;

      default:
        buildingLogger.warn('Unhandled input command type', { command });
    }
  }

  private handleCollectResourceCommand(command: InputCommand): void {
    if (command.type !== InputCommandType.COLLECT_RESOURCE) {
      return;
    }

    const { position } = command as CollectResourceCommand;
    const origin = position ?? this.state.playerPos;

    const minX = Math.max(0, origin.x - BELT_COLLECT_RADIUS);
    const maxX = Math.min(
      GAME_CONFIG.WORLD_WIDTH - 1,
      origin.x + BELT_COLLECT_RADIUS
    );
    const minY = Math.max(0, origin.y - BELT_COLLECT_RADIUS);
    const maxY = Math.min(
      GAME_CONFIG.WORLD_HEIGHT - 1,
      origin.y + BELT_COLLECT_RADIUS
    );

    let updatedInventory = this.state.playerInventory;
    const newBeltContentLayer = { ...this.state.beltContentLayer };
    let inventoryChanged = false;
    let beltLayerChanged = false;
    const processedChestCenters = new Set<string>();

    for (let x = minX; x <= maxX; x += 1) {
      for (let y = minY; y <= maxY; y += 1) {
        const key = `${x},${y}`;

        const { building, centerPosition } = getBuildingAtPosition(
          x,
          y,
          this.state.buildingLayer
        );

        if (building && (building.stackCapacity ?? 0) > 0) {
          const chestCenter = centerPosition ?? { x, y };
          const chestKey = `${chestCenter.x},${chestCenter.y}`;

          if (!processedChestCenters.has(chestKey)) {
            processedChestCenters.add(chestKey);

            const chestResult = this.collectChestInventory(chestCenter);

            if (chestResult.success) {
              let chestAddedItems = false;

              (
                Object.entries(chestResult.addedItems) as Array<
                  [ItemType, number]
                >
              ).forEach(([itemType, amount]) => {
                const normalizedAmount = amount ?? 0;
                if (normalizedAmount <= 0) {
                  return;
                }

                const addResult = addItemToPlayerInventory(
                  updatedInventory,
                  itemType,
                  normalizedAmount
                );
                updatedInventory = addResult.inventory;
                if (addResult.added > 0) {
                  inventoryChanged = true;
                  chestAddedItems = true;
                }
              });

              if (!chestAddedItems && chestResult.partial) {
                inventoryChanged = true;
              }
            }
          }
        }

        const beltItems = this.state.beltContentLayer[key];

        if (!beltItems || beltItems.length === 0) {
          continue;
        }

        const remainingItems: BeltItem[] = [];
        let tileChanged = false;

        for (const item of beltItems) {
          const { inventory, added } = addItemToPlayerInventory(
            updatedInventory,
            item.item,
            1
          );

          if (added > 0) {
            updatedInventory = inventory;
            inventoryChanged = true;
            tileChanged = true;
            beltLogger.debug('Collected belt item', {
              position: { x, y },
              item: item.item,
            });
          } else {
            remainingItems.push(item);
          }
        }

        if (!tileChanged) {
          continue;
        }

        beltLayerChanged = true;

        if (remainingItems.length > 0) {
          newBeltContentLayer[key] = remainingItems;
        } else {
          delete newBeltContentLayer[key];
        }
      }
    }

    if (!beltLayerChanged && !inventoryChanged) {
      return;
    }

    this.state = {
      ...this.state,
      playerInventory: inventoryChanged
        ? updatedInventory
        : this.state.playerInventory,
      beltContentLayer: beltLayerChanged
        ? newBeltContentLayer
        : this.state.beltContentLayer,
    };
  }

  // Handle movement commands
  private handleMoveCommand(command: InputCommand): void {
    if (command.type !== InputCommandType.MOVE) return;

    const moveCommand = command as MoveCommand;
    const { direction, isRepeat } = moveCommand;

    // Non-repeat keys (fresh key presses) bypass cooldown and clear queue
    if (!isRepeat) {
      // Clear the movement queue for responsive fresh key presses
      this.movementQueue = [];
      // Execute movement immediately, bypassing cooldown
      this.executeMovement(direction);
      return;
    }

    // For repeat events, check cooldown and queue state
    const currentTick = this.getCurrentTick();
    const isOnCooldown =
      currentTick - this.state.lastMovementTick <
      TICK_INTERVALS.PLAYER_MOVEMENT;

    // Queue repeat events if on cooldown or queue has items
    if (isOnCooldown || this.movementQueue.length > 0) {
      this.movementQueue.push({
        direction,
        timestamp: currentTick,
      });
      return;
    }

    // Execute repeat movement immediately if not on cooldown and queue is empty
    this.executeMovement(direction);
  }

  // Execute a movement in the given direction
  private executeMovement(direction: { x: number; y: number }): void {
    const { x: deltaX, y: deltaY } = direction;

    const currentPos = this.state.playerPos;
    const potentialX = Math.max(
      1,
      Math.min(GAME_CONFIG.WORLD_WIDTH - 2, currentPos.x + deltaX)
    );
    const potentialY = Math.max(
      1,
      Math.min(GAME_CONFIG.WORLD_HEIGHT - 2, currentPos.y + deltaY)
    );

    // Update facing direction based on input
    let newFacing: PlayerFacing = this.state.playerFacing;
    if (deltaX > 0) newFacing = 'right';
    else if (deltaX < 0) newFacing = 'left';
    else if (deltaY > 0) newFacing = 'down';
    else if (deltaY < 0) newFacing = 'up';

    // Check if player can move to the new position using collision system
    if (potentialX !== currentPos.x || potentialY !== currentPos.y) {
      const playerEntity = createPlayerEntity(this.state);
      const updatedPlayer = updateEntityPosition(playerEntity, {
        x: potentialX,
        y: potentialY,
      });

      const collisionResult = this.collisionService.canMoveTo(
        potentialX,
        potentialY,
        updatedPlayer
      );

      // Log collision details for debugging
      logPlayerMovementCollision(
        this.collisionService,
        currentPos,
        { x: potentialX, y: potentialY },
        collisionResult
      );

      // Also log what entities are at the target position
      logCollisionTest(
        this.collisionService,
        potentialX,
        potentialY,
        'player_movement'
      );

      if (collisionResult.canMove) {
        // Move player to new position
        this.movePlayer(potentialX, potentialY);

        // Update collision system with new player position
        this.collisionService.updateEntity(updatedPlayer);

        inputLogger.debug('Player moved with collision check', {
          from: currentPos,
          to: { x: potentialX, y: potentialY },
          facing: newFacing,
        });
      } else {
        // Cannot move - just update facing direction
        this.state = { ...this.state, playerFacing: newFacing };

        inputLogger.debug('Player movement blocked by collision', {
          targetPos: { x: potentialX, y: potentialY },
          reason: collisionResult.reason,
          blockingEntities: collisionResult.blockingEntities.map(e => ({
            id: e.id,
            type: e.type,
          })),
        });
      }
    } else {
      // Position didn't change (world boundaries) - just update facing
      this.state = { ...this.state, playerFacing: newFacing };
    }
  }

  // Process movement queue - execute queued movements when cooldown expires
  private processMovementQueue(): void {
    // Check if we can process the next movement
    const currentTick = this.getCurrentTick();
    const canMove =
      currentTick - this.state.lastMovementTick >=
      TICK_INTERVALS.PLAYER_MOVEMENT;

    if (!canMove || this.movementQueue.length === 0) {
      return;
    }

    // Get and execute the next movement from the queue
    const nextMovement = this.movementQueue.shift();
    if (nextMovement) {
      this.executeMovement(nextMovement.direction);

      inputLogger.debug('Processed queued movement', {
        direction: nextMovement.direction,
        queueLength: this.movementQueue.length,
        queuedAt: nextMovement.timestamp,
        executedAt: currentTick,
      });
    }
  }

  // Handle take action command - contextual action based on what player is facing
  private handleTakeActionCommand(command: InputCommand): void {
    if (command.type !== InputCommandType.TAKE_ACTION) return;
    const takeActionCommand = command as TakeActionCommand;
    inputLogger.debug('Processing TAKE_ACTION command in GameEngine', {
      playerPos: this.state.playerPos,
      playerFacing: this.state.playerFacing,
      buildMenuOpen: this.state.buildMenuOpen,
      selectedBuilding: this.state.selectedBuilding?.name,
      targetOverride: takeActionCommand.target,
    });

    // If we're in build mode, place the selected building
    if (this.state.selectedBuilding) {
      const targetPos = this.getBuildingTargetLocation(
        this.state.selectedBuilding
      );
      const success = this.placeBuilding(
        targetPos.x,
        targetPos.y,
        this.state.selectedBuilding
      );

      if (success) {
        inputLogger.debug('TAKE_ACTION: Placed building', {
          building: this.state.selectedBuilding.name,
          position: targetPos,
        });
      }
      return;
    }

    // Determine target position - allow optional overrides from commands (e.g., mouse clicks)
    let targetPos: Position | null = null;
    if (takeActionCommand.target) {
      targetPos = takeActionCommand.target;
    }

    if (!targetPos) {
      targetPos = this.getTargetPosition();
    }

    // Get tile at target position
    const targetTile = this.getTile(
      targetPos.x,
      targetPos.y,
      this.state.gameMap,
      this.state.buildingLayer
    );

    inputLogger.debug('TAKE_ACTION: Analyzing target', {
      targetPos,
      targetTile,
      isResourceNode:
        targetTile === GAME_CONFIG.IRON_NODE_CHARACTER ||
        targetTile === GAME_CONFIG.COPPER_NODE_CHARACTER,
    });

    // Check if target is a resource node and we're not already in build mode
    if (
      !this.state.selectedBuilding &&
      (targetTile === GAME_CONFIG.IRON_NODE_CHARACTER ||
        targetTile === GAME_CONFIG.COPPER_NODE_CHARACTER)
    ) {
      inputLogger.debug('TAKE_ACTION: Opening resource node menu', {
        targetTile,
        targetPos,
      });
      this.openResourceNodeMenu(targetPos);
      return;
    }

    // Check if target is a building that can be configured
    const { building: buildingAtTarget } = getBuildingAtPosition(
      targetPos.x,
      targetPos.y,
      this.state.buildingLayer
    );
    if (buildingAtTarget) {
      const isConfigurableBuilding =
        buildingAtTarget.id === BuildingType.ASSEMBLER ||
        buildingAtTarget.id === BuildingType.FACTORY ||
        buildingAtTarget.id === BuildingType.RESEARCH_FACILITY ||
        buildingAtTarget.category === 'mining' ||
        buildingAtTarget.category === 'power' ||
        (buildingAtTarget.stackCapacity ?? 0) > 0;

      if (isConfigurableBuilding) {
        // Handle building status menu opening directly
        let menuPosition = targetPos;

        // For assemblers, find the center position
        if (
          buildingAtTarget.id === BuildingType.ASSEMBLER ||
          buildingAtTarget.id === BuildingType.FACTORY ||
          buildingAtTarget.id === BuildingType.RESEARCH_FACILITY
        ) {
          const centerPosition = findBuildingCenter(
            targetPos.x,
            targetPos.y,
            buildingAtTarget,
            this.state.buildingLayer
          );
          if (centerPosition) {
            menuPosition = centerPosition;
          } else {
            buildingLogger.warn('Cannot determine building center position', {
              position: targetPos,
              building: buildingAtTarget.name,
            });
            return;
          }
        }

        // Open the building status menu
        this.openBuildingStatusMenu(menuPosition);

        inputLogger.debug('TAKE_ACTION: Opened building status menu', {
          building: buildingAtTarget.name,
          position: menuPosition,
        });
        return;
      }
    }

    inputLogger.debug('TAKE_ACTION: No valid target for action', {
      targetPos,
      targetTile,
    });
  }

  // Handle toggle build menu command
  private handleToggleBuildMenuCommand(): void {
    // Toggle logic: false -> true -> false
    // autoBuildOnSelect is treated as true for toggle purposes
    const isCurrentlyOpen = this.state.buildMenuOpen !== false;
    const newState = isCurrentlyOpen ? false : true;

    this.state = {
      ...this.state,
      buildMenuOpen: newState,
      selectedCategory: null,
      selectedBuilding: null,
    };

    inputLogger.debug('Toggled build menu', {
      previousState: isCurrentlyOpen
        ? this.state.buildMenuOpen === 'autoBuildOnSelect'
          ? 'autoBuildOnSelect'
          : 'true'
        : 'false',
      buildMenuOpen: newState,
    });

    this.emitEvent('stateChanged');
  }

  // Handle select build category command
  private handleSelectBuildCategoryCommand(command: InputCommand): void {
    if (command.type !== InputCommandType.SELECT_BUILD_CATEGORY) return;

    const { category } = command as { category: BuildingCategory | null };

    this.state = {
      ...this.state,
      selectedCategory: category,
      selectedBuilding: null,
    };

    inputLogger.debug('Selected build category', {
      category,
    });

    this.emitEvent('stateChanged');
  }

  // Handle select building command
  private handleSelectBuildingCommand(command: InputCommand): void {
    if (command.type !== InputCommandType.SELECT_BUILDING) return;

    const { building } = command as { building: Building };

    // Check if we're in autoBuildOnSelect mode
    const shouldAutoBuild = this.state.buildMenuOpen === 'autoBuildOnSelect';

    this.state = {
      ...this.state,
      selectedBuilding: building,
      buildMenuOpen: false, // Close build menu after selecting building
      quickBeltPreview: null,
    };

    this.quickBeltAnchor = null;

    inputLogger.debug('Selected building', {
      building: building.name,
      wasAutoBuildMode: shouldAutoBuild,
      buildMenuClosed: true,
    });

    this.emitEvent('stateChanged');
  }

  // Handle UI menu toggle commands
  private handleToggleSaveMenuCommand(): void {
    this.state = {
      ...this.state,
      saveMenuOpen: !this.state.saveMenuOpen,
    };

    inputLogger.debug('Toggled save menu', {
      saveMenuOpen: this.state.saveMenuOpen,
    });

    this.emitEvent('stateChanged');
  }

  private handleToggleDebugMenuCommand(): void {
    const newDebugMenuOpen = !this.state.debugMenuOpen;

    this.state = {
      ...this.state,
      debugMenuOpen: newDebugMenuOpen,
    };

    // Save debug menu UI state to localStorage
    saveDebugMenuUIState({ debugMenuOpen: newDebugMenuOpen });

    inputLogger.debug('Toggled debug menu', {
      debugMenuOpen: this.state.debugMenuOpen,
    });

    this.emitEvent('stateChanged');
  }

  private handleToggleAchievementMenuCommand(): void {
    this.state = {
      ...this.state,
      achievementMenuOpen: !this.state.achievementMenuOpen,
    };

    inputLogger.debug('Toggled achievement menu', {
      achievementMenuOpen: this.state.achievementMenuOpen,
    });

    this.emitEvent('stateChanged');
  }

  private handleToggleHelpMenuCommand(): void {
    this.state = {
      ...this.state,
      helpMenuOpen: !this.state.helpMenuOpen,
    };

    inputLogger.debug('Toggled help menu', {
      helpMenuOpen: this.state.helpMenuOpen,
    });

    this.emitEvent('stateChanged');
  }

  private handleToggleResearchMenuCommand(): void {
    const nextOpen = !this.state.researchMenuOpen;
    this.setResearchMenuOpen(nextOpen);
    inputLogger.debug('Toggled research menu', {
      researchMenuOpen: nextOpen,
    });
  }

  private handleToggleProductionMenuCommand(): void {
    const nextOpen = !this.state.productionMenuOpen;
    this.setProductionMenuOpen(nextOpen);
    inputLogger.debug('Toggled production menu', {
      productionMenuOpen: nextOpen,
    });
  }

  private handleToggleLeftPanelCommand(): void {
    this.state = {
      ...this.state,
      leftPanelOpen: !this.state.leftPanelOpen,
    };

    inputLogger.debug('Toggled left panel', {
      leftPanelOpen: this.state.leftPanelOpen,
    });

    this.emitEvent('stateChanged');
  }

  // Handle ESC key - close top-most open menu based on priority
  private handleCloseTopMenuCommand(): void {
    // Priority order: Save > Help > Debug > Achievement > Building > Build Mode > Build Menu > Left Panel
    if (this.state.saveMenuOpen) {
      this.state = { ...this.state, saveMenuOpen: false };
      inputLogger.debug('ESC: Closed save menu');
    } else if (this.state.helpMenuOpen) {
      this.state = { ...this.state, helpMenuOpen: false };
      inputLogger.debug('ESC: Closed help menu');
    } else if (this.state.researchMenuOpen) {
      this.state = { ...this.state, researchMenuOpen: false };
      inputLogger.debug('ESC: Closed research menu');
    } else if (this.state.productionMenuOpen) {
      this.state = { ...this.state, productionMenuOpen: false };
      inputLogger.debug('ESC: Closed production menu');
    } else if (this.state.debugMenuOpen) {
      this.state = { ...this.state, debugMenuOpen: false };
      inputLogger.debug('ESC: Closed debug menu');
    } else if (this.state.achievementMenuOpen) {
      this.state = { ...this.state, achievementMenuOpen: false };
      inputLogger.debug('ESC: Closed achievement menu');
    } else if (this.state.resourceNodeMenu) {
      this.closeResourceNodeMenu();
      inputLogger.debug('ESC: Closed resource node menu');
    } else if (this.state.buildingStatusMenuTarget) {
      this.closeBuildingStatusMenu();
      inputLogger.debug('ESC: Closed building status menu');
    } else if (this.state.selectedBuilding) {
      // Exit build mode if in build mode
      this.state = {
        ...this.state,
        selectedBuilding: null,
      };
      inputLogger.debug('ESC: Exited build mode');
    } else if (this.state.selectedCategory) {
      // Clear selected category
      this.state = {
        ...this.state,
        selectedCategory: null,
        selectedBuilding: null,
      };
      inputLogger.debug('ESC: Cleared selected category');
    } else if (this.state.buildMenuOpen) {
      this.state = { ...this.state, buildMenuOpen: false };
      inputLogger.debug('ESC: Closed build menu');
    } else if (this.state.leftPanelOpen) {
      this.state = { ...this.state, leftPanelOpen: false };
      inputLogger.debug('ESC: Closed left panel');
    } else {
      inputLogger.debug('ESC: No menus open to close');
      return; // No state change, no need to emit event
    }

    this.emitEvent('stateChanged');
  }

  // Handle building deconstruction commands
  private handleBuildDeconstructCommand(command: InputCommand): void {
    if (command.type !== InputCommandType.BUILD_DECONSTRUCT) return;

    const targetPos = this.getTargetPosition();
    this.deconstructBuilding(targetPos.x, targetPos.y);
  }

  // Building deconstruction with inventory refunds
  deconstructBuilding(x: number, y: number): boolean {
    const { building, centerPosition } = getBuildingAtPosition(
      x,
      y,
      this.state.buildingLayer
    );

    if (!building || !centerPosition) {
      return false; // Nothing to deconstruct
    }

    const key = `${centerPosition.x},${centerPosition.y}`;
    const buildingCharacter = this.state.buildingLayer[key];

    if (!buildingCharacter) {
      return false;
    }

    // Remove building
    const newBuildingLayer = { ...this.state.buildingLayer };
    delete newBuildingLayer[key];

    // Clean up building-specific state
    const newMinerTimerLayer = { ...this.state.minerTimerLayer };
    const newMinerOutputBufferLayer = {
      ...this.state.minerOutputBufferLayer,
    };
    const newAssemblerInventoryLayer = {
      ...this.state.assemblerInventoryLayer,
    };
    const newChestInventoryLayer = { ...this.state.chestInventoryLayer };
    const newBeltContentLayer = { ...this.state.beltContentLayer };
    const newRejectedItemsLayer = { ...this.state.rejectedItemsLayer };

    delete newMinerTimerLayer[key];
    delete newMinerOutputBufferLayer[key];

    const refundedItems: Partial<Record<ItemType, number>> = {};
    let updatedPlayerInventory = this.state.playerInventory;

    const addRefundedItem = (itemType: ItemType, amount: number): void => {
      if (amount <= 0) {
        return;
      }

      const { inventory, added } = addItemToPlayerInventory(
        updatedPlayerInventory,
        itemType,
        amount
      );

      updatedPlayerInventory = inventory;

      if (added > 0) {
        refundedItems[itemType] = (refundedItems[itemType] ?? 0) + added;
      }

      if (added < amount) {
        buildingLogger.warn(
          'Unable to refund full amount to player inventory',
          {
            building: building.id,
            itemType,
            attempted: amount,
            added,
          }
        );
      }
    };

    // Refund building cost items
    const costItems = building.cost.items ?? [];
    for (const requirement of costItems) {
      addRefundedItem(requirement.item, requirement.amount);
    }

    // Collect assembler inventory contents
    const assemblerInventory = newAssemblerInventoryLayer[key];
    if (assemblerInventory) {
      (
        Object.entries(assemblerInventory.items) as Array<[ItemType, number]>
      ).forEach(([itemType, amount]) => {
        if (amount > 0) {
          addRefundedItem(itemType, amount);
        }
      });
    }
    delete newAssemblerInventoryLayer[key];

    // Collect chest inventory contents
    const chestInventory = newChestInventoryLayer[key];
    if (chestInventory) {
      (
        Object.entries(chestInventory.items) as Array<[ItemType, number]>
      ).forEach(([itemType, amount]) => {
        if (amount > 0) {
          addRefundedItem(itemType, amount);
        }
      });
    }
    delete newChestInventoryLayer[key];

    // Collect belt contents (items on the belt tile)
    const beltItems = newBeltContentLayer[key];
    if (beltItems) {
      beltItems.forEach(item => addRefundedItem(item.item, 1));
    }
    delete newBeltContentLayer[key];

    delete newRejectedItemsLayer[key];

    if (Object.keys(refundedItems).length > 0) {
      buildingLogger.info('Refunded items from deconstructed building', {
        building: building.id,
        refundedItems,
      });
    }

    this.state = {
      ...this.state,
      buildingLayer: newBuildingLayer,
      minerTimerLayer: newMinerTimerLayer,
      minerOutputBufferLayer: newMinerOutputBufferLayer,
      assemblerInventoryLayer: newAssemblerInventoryLayer,
      chestInventoryLayer: newChestInventoryLayer,
      beltContentLayer: newBeltContentLayer,
      rejectedItemsLayer: newRejectedItemsLayer,
      playerInventory: updatedPlayerInventory,
    };

    this.updateCollisionSystem();

    this.recalculatePowerNetwork(false);

    this.emitEvent('buildingDeconstructed');
    return true;
  }

  // Handle save game commands
  private handleSaveGameCommand(): void {
    // This will be handled by UI layer since it involves React state management
    // The GameEngine doesn't handle persistence directly
  }

  // Zoom controls
  zoomIn(): void {
    this.state = {
      ...this.state,
      zoomLevel: Math.min(3, this.state.zoomLevel + 0.25),
    };
    this.emitEvent('stateChanged');
  }

  zoomOut(): void {
    this.state = {
      ...this.state,
      zoomLevel: Math.max(0.5, this.state.zoomLevel - 0.25),
    };
    this.emitEvent('stateChanged');
  }

  resetZoom(): void {
    this.state = {
      ...this.state,
      zoomLevel: 2,
    };
    this.emitEvent('stateChanged');
  }

  resetResearch(researchId: ResearchId): void {
    const current = this.state.researchState;

    const unlockedResearch = { ...current.unlockedResearch };
    delete unlockedResearch[researchId];

    const progress = { ...current.progress };
    delete progress[researchId];

    const repeatableResearchLevels = { ...current.repeatableResearchLevels };
    delete repeatableResearchLevels[researchId];

    const updatedState = setResearchQueue(
      {
        ...current,
        unlockedResearch,
        progress,
        repeatableResearchLevels,
        selectedResearch: null,
      },
      current.queue.filter(id => id !== researchId)
    );

    this.state = {
      ...this.state,
      researchState: updatedState,
    };

    this.emitEvent('stateChanged');
  }

  resetAllResearch(): void {
    this.state = {
      ...this.state,
      researchState: createInitialResearchState(),
    };

    this.emitEvent('stateChanged');
  }

  // Clear all belt items (debug command)
  clearBeltItems(): void {
    this.state = {
      ...this.state,
      beltContentLayer: {},
    };
    this.emitEvent('stateChanged');
    buildingLogger.info('Belt items cleared for debugging');
  }

  // Set hasEnabledDebug flag (for achievement/cheat tracking)
  setHasEnabledDebug(enabled: boolean): void {
    const normalizedMultiplier = this.normalizeTickSpeedMultiplier(
      this.state.tickSpeedMultiplier,
      enabled
    );

    this.state = {
      ...this.state,
      hasEnabledDebug: enabled,
      tickSpeedMultiplier: normalizedMultiplier,
    };

    // Update achievement system configuration
    this.achievementSystem.updateConfig({ achievementsDisabled: enabled });

    this.emitEvent('stateChanged');
    buildingLogger.info('Debug mode enabled status changed', { enabled });
  }

  // Achievement system methods
  private getAchievementProgress(
    type: AchievementType
  ): Achievement['progress'] | undefined {
    switch (type) {
      case AchievementType.DATA_DRIP: {
        const target = GameEngine.RESEARCH_DATA_TARGET;
        const current = Math.min(this.totalResearchDataProduced, target);
        return { current, target };
      }

      case AchievementType.APPLIED_ENTROPY: {
        const target = GameEngine.RESEARCH_COMPLETION_TARGET;
        const current = Math.min(this.getUnlockedResearchCount(), target);
        return { current, target };
      }

      case AchievementType.RESOURCE_BARON: {
        const target = GameEngine.ORE_EXTRACTION_TARGET;
        const current = Math.min(this.totalOreExtracted, target);
        return { current, target };
      }

      case AchievementType.IRON_REVOLUTION: {
        const target = GameEngine.IRON_PLATE_TARGET;
        const current = Math.min(this.totalIronPlatesProduced, target);
        return { current, target };
      }

      default:
        return undefined;
    }
  }

  private buildAchievementsWithProgress(): Achievement[] {
    return this.achievementSystem.getAchievements().map(achievement => {
      const progress = this.getAchievementProgress(achievement.id);
      return progress ? { ...achievement, progress } : { ...achievement };
    });
  }

  private refreshAchievementProgress(): void {
    this.state.achievements = this.buildAchievementsWithProgress();
  }

  private isAchievementUnlocked(type: AchievementType): boolean {
    return this.achievementSystem.getAchievement(type)?.unlocked ?? false;
  }

  getAchievements(): Achievement[] {
    return this.buildAchievementsWithProgress();
  }

  triggerAchievementCheck(
    context: AchievementContext,
    options?: { suppressEvent?: boolean }
  ): void {
    const suppressEvent = options?.suppressEvent ?? false;
    const results = this.achievementSystem.checkAchievements(context);

    if (results.length > 0) {
      // Update local state with new achievements
      this.state = {
        ...this.state,
        achievementSaveData: this.achievementSystem.getSaveData(),
      };
      this.refreshAchievementProgress();

      if (!suppressEvent) {
        this.emitEvent('stateChanged');
      }

      // Log achievement unlocks
      for (const result of results) {
        if (result.isNewlyUnlocked && result.message) {
          buildingLogger.info('Achievement unlocked!', {
            name: result.achievement.name,
            description: result.achievement.description,
          });
        }
      }
    }
  }

  // Load achievements from save data
  loadAchievements(achievementSaveData: AchievementSaveData): void {
    this.achievementSystem.loadAchievements(achievementSaveData);
    this.state = {
      ...this.state,
      achievementSaveData: this.achievementSystem.getSaveData(),
    };
    this.refreshAchievementProgress();
  }

  // Game reset
  resetGame(): void {
    // Use shared initialization logic to properly regenerate entities
    this.initializeGameState();

    this.emitEvent('stateChanged');

    // Trigger "First Steps" achievement after resetting to a fresh game state
    this.triggerAchievementCheck({ isNewGame: true });
  }

  private performPrestigeReset(
    amount: number,
    source: 'launch' | 'force'
  ): void {
    const normalizedAmount = Number.isFinite(amount) ? Math.floor(amount) : 0;

    if (normalizedAmount <= 0) {
      buildingLogger.warn('Attempted prestige reset with invalid amount', {
        amount,
        source,
      });
      return;
    }

    const currentPrestigePoints = this.state.prestigePoints ?? 0;
    const currentPrestigeCount = this.state.prestigeCount ?? 0;
    const nextPrestigePoints = currentPrestigePoints + normalizedAmount;
    const nextPrestigeCount = currentPrestigeCount + normalizedAmount;

    this.initializeGameState({
      prestigePoints: nextPrestigePoints,
      prestigeCount: nextPrestigeCount,
      hasEnabledDebug: this.state.hasEnabledDebug,
    });

    const logPayload = {
      addedPrestige: normalizedAmount,
      prestigePoints: nextPrestigePoints,
      prestigeCount: nextPrestigeCount,
      source,
    };

    if (source === 'launch') {
      buildingLogger.info('Rocket launched - prestige awarded', logPayload);
    } else {
      buildingLogger.info(
        'Force prestige triggered via debug tools',
        logPayload
      );
    }

    this.emitEvent('stateChanged');
  }

  launchRocket(): void {
    this.performPrestigeReset(1, 'launch');
  }

  forcePrestige(amount: number = 1): void {
    this.performPrestigeReset(amount, 'force');
  }

  // Cleanup method
  destroy(): void {
    this.inputManager.stopListening();
    this.eventListeners.clear();
    this.collisionService.clear();
    this.entityManager.clear();
    buildingLogger.info('GameEngine destroyed and cleaned up');
  }

  // Utility methods
  updateBiome(biomeName: string): void {
    this.state = {
      ...this.state,
      currentBiome: biomeName,
    };
    this.emitEvent('stateChanged');
  }

  setHoverTargetPosition(position: Position | null): void {
    let normalized: Position | null = null;

    if (position) {
      const { x, y } = position;
      if (
        Number.isFinite(x) &&
        Number.isFinite(y) &&
        x >= 0 &&
        x < GAME_CONFIG.WORLD_WIDTH &&
        y >= 0 &&
        y < GAME_CONFIG.WORLD_HEIGHT
      ) {
        normalized = { x: Math.round(x), y: Math.round(y) };
      }
    }

    if (this.positionsEqual(this.state.hoverTargetPosition, normalized)) {
      return;
    }

    this.state = {
      ...this.state,
      hoverTargetPosition: normalized,
    };

    this.emitEvent('hoverTargetChanged');
  }

  // Get target position based on player facing (always one tile in front)
  getTargetPosition(building?: Building): Position {
    if (this.state.hoverTargetPosition) {
      return this.state.hoverTargetPosition;
    }

    const { x, y } = this.state.playerPos;
    const facing = this.state.playerFacing;

    const shiftHeld =
      this.inputManager.isKeyPressed('Shift') ||
      this.inputManager.isKeyPressed('ShiftLeft') ||
      this.inputManager.isKeyPressed('ShiftRight');

    // If building normally places under the player, only do so when shift is not held
    if (building?.isPlaceUnderPlayer && !shiftHeld) {
      return { x, y };
    }

    switch (facing) {
      case 'up':
        return { x, y: y - 1 };
      case 'down':
        return { x, y: y + 1 };
      case 'left':
        return { x: x - 1, y };
      case 'right':
        return { x: x + 1, y };
      default:
        return { x, y: y + 1 };
    }
  }

  // Get building target location (where building center should be placed)
  getBuildingTargetLocation(building: Building): Position {
    if (this.state.hoverTargetPosition) {
      return this.state.hoverTargetPosition;
    }

    const targetPos = this.getTargetPosition(building);
    const size = building.size || { width: 1, height: 1 };

    // For 1x1 buildings, place at target position
    if (size.width === 1 && size.height === 1) {
      return targetPos;
    }

    // For multi-tile buildings, calculate where center should be
    // to avoid overlapping with player
    const { x, y } = this.state.playerPos;
    const facing = this.state.playerFacing;
    const centerOffset = Math.ceil(size.width / 2);

    switch (facing) {
      case 'up':
        return { x, y: y - centerOffset };
      case 'down':
        return { x, y: y + centerOffset };
      case 'left':
        return { x: x - centerOffset, y };
      case 'right':
        return { x: x + centerOffset, y };
      default:
        return { x, y: y + centerOffset };
    }
  }
}

// Global game engine instance
let gameEngineInstance: GameEngine | null = null;

export const getGameEngine = (): GameEngine => {
  if (!gameEngineInstance) {
    gameEngineInstance = new GameEngine();
  }
  return gameEngineInstance;
};

export const createGameEngine = (
  initialState?: Partial<CoreGameState>,
  options: GameEngineOptions = {}
): GameEngine => {
  gameEngineInstance = new GameEngine(initialState, options);
  return gameEngineInstance;
};

export const resetGameEngine = (): void => {
  gameEngineInstance = null;
};
